import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Check, X, ChevronRight, RotateCcw, Award, Brain, Users, Zap, Clock } from "lucide-react";
import { trackGameComplete } from "../lib/analytics";
import { applyBoost, GAME_BOOST } from "../lib/tamagotchiUtils";

const PubQuiz = ({ onBack, userData, saveUserData }) => {
  const [gameMode, setGameMode] = useState("selection"); // selection, levelSelect, playing, rapid, roundComplete, complete
  const [selectedMode, setSelectedMode] = useState(null); // "classic" or "rapid"
  const [selectedLevel, setSelectedLevel] = useState(null); // "beginner", "intermediate", "advanced"
  const [currentRound, setCurrentRound] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [roundScores, setRoundScores] = useState([]);
  const [timeLeft, setTimeLeft] = useState(15); // 15 seconds for rapid mode
  const [timerActive, setTimerActive] = useState(false);
  const [hasTrackedCompletion, setHasTrackedCompletion] = useState(false);
  const [roundAnswers, setRoundAnswers] = useState([]); // {question, options, selectedAnswer, correctAnswer, explanation, isCorrect}

  // Quiz rounds with different medical categories
  const quizRounds = useMemo(() => [
    {
      id: 1,
      title: "Anatomy Round",
      description: "Test your knowledge of human anatomy",
      color: "from-red-100 to-pink-100",
      questions: [
        {
          question: "What is the largest organ in the human body?",
          options: ["Liver", "Brain", "Skin", "Heart"],
          correctAnswer: "Skin",
          explanation: "The skin is the largest organ, covering approximately 20 square feet in adults and accounting for about 16% of total body weight."
        },
        {
          question: "How many bones are in the adult human body?",
          options: ["186", "206", "226", "246"],
          correctAnswer: "206",
          explanation: "The adult human skeleton has 206 bones. Babies are born with about 270 bones, but many fuse together as they grow."
        },
        {
          question: "Which chamber of the heart pumps oxygenated blood to the body?",
          options: ["Right atrium", "Right ventricle", "Left atrium", "Left ventricle"],
          correctAnswer: "Left ventricle",
          explanation: "The left ventricle is the thickest and most muscular chamber, pumping oxygen-rich blood through the aorta to the entire body."
        },
        {
          question: "What is the smallest bone in the human body?",
          options: ["Stapes", "Malleus", "Incus", "Phalanx"],
          correctAnswer: "Stapes",
          explanation: "The stapes (stirrup bone) in the middle ear is the smallest bone, measuring only 2.8mm in length."
        },
        {
          question: "Which organ produces insulin?",
          options: ["Liver", "Pancreas", "Kidney", "Spleen"],
          correctAnswer: "Pancreas",
          explanation: "The pancreas produces insulin in beta cells located in the islets of Langerhans. Insulin regulates blood sugar levels."
        },
        {
          question: "What is the longest bone in the human body?",
          options: ["Tibia", "Humerus", "Femur", "Fibula"],
          correctAnswer: "Femur",
          explanation: "The femur (thigh bone) is the longest and strongest bone in the human body, making up about one-quarter of a person's height."
        },
        {
          question: "Which blood vessel carries deoxygenated blood from the heart to the lungs?",
          options: ["Aorta", "Pulmonary artery", "Pulmonary vein", "Vena cava"],
          correctAnswer: "Pulmonary artery",
          explanation: "The pulmonary artery is the only artery that carries deoxygenated blood, transporting it from the right ventricle to the lungs for oxygenation."
        },
        {
          question: "How many lobes does the right lung have?",
          options: ["1", "2", "3", "4"],
          correctAnswer: "3",
          explanation: "The right lung has 3 lobes (upper, middle, lower), while the left lung has only 2 lobes to accommodate the heart."
        },
        {
          question: "What is the largest internal organ?",
          options: ["Heart", "Brain", "Liver", "Lungs"],
          correctAnswer: "Liver",
          explanation: "The liver is the largest internal organ, weighing about 1.5 kg (3.3 lbs). It performs over 500 functions including detoxification and protein synthesis."
        },
        {
          question: "Which cranial nerve controls the movement of most eye muscles?",
          options: ["Optic nerve (II)", "Oculomotor nerve (III)", "Trochlear nerve (IV)", "Abducens nerve (VI)"],
          correctAnswer: "Oculomotor nerve (III)",
          explanation: "The oculomotor nerve (CN III) controls 4 of the 6 extraocular muscles, as well as the levator palpebrae and pupil constriction."
        },
        {
          question: "What structure connects muscle to bone?",
          options: ["Ligament", "Tendon", "Cartilage", "Fascia"],
          correctAnswer: "Tendon",
          explanation: "Tendons connect muscles to bones, while ligaments connect bones to other bones. Both are made of dense connective tissue."
        },
        {
          question: "Which part of the brain controls balance and coordination?",
          options: ["Cerebrum", "Cerebellum", "Brainstem", "Hypothalamus"],
          correctAnswer: "Cerebellum",
          explanation: "The cerebellum, located at the back of the brain, coordinates voluntary movements, balance, and motor learning."
        },
        {
          question: "How many cervical vertebrae are in the human spine?",
          options: ["5", "7", "9", "12"],
          correctAnswer: "7",
          explanation: "There are 7 cervical vertebrae (C1-C7) in the neck region. Interestingly, almost all mammals have exactly 7 cervical vertebrae."
        },
        {
          question: "What is the name of the membrane that surrounds the heart?",
          options: ["Pleura", "Pericardium", "Peritoneum", "Meninges"],
          correctAnswer: "Pericardium",
          explanation: "The pericardium is a double-walled sac containing the heart and roots of the great vessels. It protects and lubricates the heart."
        },
        {
          question: "Which artery supplies blood to the heart muscle itself?",
          options: ["Aorta", "Coronary artery", "Carotid artery", "Subclavian artery"],
          correctAnswer: "Coronary artery",
          explanation: "The coronary arteries branch off the aorta and supply oxygenated blood to the heart muscle (myocardium). Blockage causes heart attacks."
        },
        {
          question: "What is the name of the large muscle that separates the chest from the abdomen?",
          options: ["Intercostal muscles", "Diaphragm", "Rectus abdominis", "Transversus abdominis"],
          correctAnswer: "Diaphragm",
          explanation: "The diaphragm is the primary muscle of respiration. When it contracts, it flattens and increases thoracic volume, drawing air into the lungs."
        },
        {
          question: "Which lobe of the brain is responsible for vision?",
          options: ["Frontal lobe", "Parietal lobe", "Temporal lobe", "Occipital lobe"],
          correctAnswer: "Occipital lobe",
          explanation: "The occipital lobe at the back of the brain contains the primary visual cortex and processes visual information."
        },
        {
          question: "How many thoracic vertebrae are in the spine?",
          options: ["7", "12", "5", "4"],
          correctAnswer: "12",
          explanation: "There are 12 thoracic vertebrae (T1-T12), each attached to a pair of ribs."
        },
        {
          question: "What is the medical term for the shoulder blade?",
          options: ["Clavicle", "Scapula", "Humerus", "Sternum"],
          correctAnswer: "Scapula",
          explanation: "The scapula is a flat triangular bone that forms the posterior part of the shoulder girdle."
        },
        {
          question: "Which nerve is responsible for the sense of smell?",
          options: ["Optic nerve", "Olfactory nerve", "Trigeminal nerve", "Facial nerve"],
          correctAnswer: "Olfactory nerve",
          explanation: "The olfactory nerve (CN I) transmits sensory information for smell from the nasal cavity to the brain."
        },
        {
          question: "What is the largest lymphoid organ in the body?",
          options: ["Thymus", "Spleen", "Tonsils", "Lymph nodes"],
          correctAnswer: "Spleen",
          explanation: "The spleen is the largest lymphoid organ, filtering blood and storing platelets and white blood cells."
        },
        {
          question: "Which valve separates the left atrium from the left ventricle?",
          options: ["Tricuspid valve", "Mitral valve", "Aortic valve", "Pulmonary valve"],
          correctAnswer: "Mitral valve",
          explanation: "The mitral (bicuspid) valve has two leaflets and prevents backflow of blood from the left ventricle to the left atrium."
        },
        {
          question: "What is the bundle of nerve fibers connecting the two hemispheres of the brain?",
          options: ["Corpus callosum", "Thalamus", "Hypothalamus", "Pons"],
          correctAnswer: "Corpus callosum",
          explanation: "The corpus callosum is the largest white matter structure in the brain, enabling communication between the left and right hemispheres."
        },
        {
          question: "Which organ is responsible for filtering blood and producing urine?",
          options: ["Liver", "Kidney", "Bladder", "Spleen"],
          correctAnswer: "Kidney",
          explanation: "The kidneys filter about 180 liters of blood daily, removing waste products and excess fluid to produce urine."
        },
        {
          question: "What type of joint is the hip joint?",
          options: ["Hinge joint", "Pivot joint", "Ball-and-socket joint", "Saddle joint"],
          correctAnswer: "Ball-and-socket joint",
          explanation: "The hip joint is a ball-and-socket joint allowing movement in multiple planes, similar to the shoulder joint."
        },
        {
          question: "Which cranial nerve controls facial expression?",
          options: ["Trigeminal (CN V)", "Facial (CN VII)", "Vestibulocochlear (CN VIII)", "Glossopharyngeal (CN IX)"],
          correctAnswer: "Facial (CN VII)",
          explanation: "The facial nerve (CN VII) controls the muscles of facial expression. Damage causes Bell's palsy — ipsilateral facial paralysis affecting both upper and lower face."
        },
        {
          question: "Which nerve is compressed in carpal tunnel syndrome?",
          options: ["Radial nerve", "Ulnar nerve", "Median nerve", "Musculocutaneous nerve"],
          correctAnswer: "Median nerve",
          explanation: "The median nerve passes through the carpal tunnel and supplies the lateral palm and first 3½ fingers. Compression causes pain, tingling, and weakness of the thenar muscles."
        },
        {
          question: "What is the first part of the small intestine?",
          options: ["Jejunum", "Ileum", "Duodenum", "Caecum"],
          correctAnswer: "Duodenum",
          explanation: "The duodenum is the first and shortest (25 cm) part of the small intestine, receiving chyme from the stomach along with bile and pancreatic enzymes at the ampulla of Vater."
        },
        {
          question: "Which organ sits directly above each kidney?",
          options: ["Liver", "Adrenal gland", "Spleen", "Pancreas"],
          correctAnswer: "Adrenal gland",
          explanation: "The adrenal (suprarenal) glands sit like a cap on top of each kidney. They have a cortex (cortisol, aldosterone, androgens) and medulla (adrenaline, noradrenaline)."
        },
        {
          question: "How many lumbar vertebrae are in the spine?",
          options: ["4", "5", "6", "7"],
          correctAnswer: "5",
          explanation: "There are 5 lumbar vertebrae (L1-L5) in the lower back. They are the largest vertebrae and the common site of disc prolapse, especially L4/L5 and L5/S1."
        },
        {
          question: "What membrane lines the abdominal cavity and covers most abdominal organs?",
          options: ["Pleura", "Pericardium", "Peritoneum", "Meninges"],
          correctAnswer: "Peritoneum",
          explanation: "The peritoneum is a serous membrane with a parietal layer (lines cavity wall) and visceral layer (covers organs). Inflammation is peritonitis; free fluid is ascites."
        },
        {
          question: "Where does the appendix attach to the large intestine?",
          options: ["Sigmoid colon", "Ascending colon", "Caecum", "Transverse colon"],
          correctAnswer: "Caecum",
          explanation: "The vermiform appendix attaches to the posteromedial wall of the caecum at the convergence of the taeniae coli. McBurney's point marks its surface projection."
        },
        {
          question: "What is the longest vein in the body, commonly used in coronary bypass surgery?",
          options: ["Femoral vein", "Great saphenous vein", "Basilic vein", "Internal jugular vein"],
          correctAnswer: "Great saphenous vein",
          explanation: "The great saphenous vein runs from the medial foot to the saphenofemoral junction in the groin. It is commonly harvested as a conduit for CABG (coronary artery bypass grafting)."
        },
        {
          question: "What is the name of the innermost membrane directly covering the brain surface?",
          options: ["Dura mater", "Arachnoid mater", "Pia mater", "Falx cerebri"],
          correctAnswer: "Pia mater",
          explanation: "The pia mater is the innermost, delicate meningeal layer that closely follows the brain's contours. The three meninges from outside in: dura, arachnoid, pia."
        },
        {
          question: "Which structure in the eye is responsible for focusing light onto the retina?",
          options: ["Cornea", "Iris", "Lens", "Vitreous humour"],
          correctAnswer: "Lens",
          explanation: "The lens focuses light onto the retina through accommodation (changing shape via the ciliary muscles). The cornea provides about 2/3 of refractive power, but the lens is adjustable."
        }
      ]
    },
    {
      id: 2,
      title: "Pharmacology Round",
      description: "Drug classes and mechanisms",
      color: "from-blue-100 to-cyan-100",
      questions: [
        {
          question: "What is the mechanism of action of aspirin?",
          options: ["Beta blocker", "ACE inhibitor", "COX inhibitor", "Calcium channel blocker"],
          correctAnswer: "COX inhibitor",
          explanation: "Aspirin irreversibly inhibits cyclooxygenase (COX) enzymes, reducing prostaglandin synthesis and providing anti-inflammatory and antiplatelet effects."
        },
        {
          question: "Which class of drugs is Lisinopril?",
          options: ["Beta blocker", "ACE inhibitor", "ARB", "Diuretic"],
          correctAnswer: "ACE inhibitor",
          explanation: "Lisinopril is an ACE (angiotensin-converting enzyme) inhibitor used to treat hypertension and heart failure by blocking the conversion of angiotensin I to angiotensin II."
        },
        {
          question: "What is the antidote for warfarin overdose?",
          options: ["Protamine sulfate", "Naloxone", "Vitamin K", "N-acetylcysteine"],
          correctAnswer: "Vitamin K",
          explanation: "Vitamin K reverses warfarin's anticoagulant effect. Warfarin inhibits vitamin K-dependent clotting factors (II, VII, IX, X)."
        },
        {
          question: "Which antibiotic class does Amoxicillin belong to?",
          options: ["Macrolides", "Fluoroquinolones", "Penicillins", "Cephalosporins"],
          correctAnswer: "Penicillins",
          explanation: "Amoxicillin is a beta-lactam antibiotic in the penicillin class. It works by inhibiting bacterial cell wall synthesis."
        },
        {
          question: "What is the mechanism of Metformin?",
          options: ["Increases insulin secretion", "Decreases hepatic glucose production", "Increases glucose absorption", "Blocks glucose reabsorption"],
          correctAnswer: "Decreases hepatic glucose production",
          explanation: "Metformin primarily works by decreasing hepatic glucose production and improving insulin sensitivity, without increasing insulin secretion."
        },
        {
          question: "What is the antidote for opioid overdose?",
          options: ["Flumazenil", "Naloxone", "Atropine", "Epinephrine"],
          correctAnswer: "Naloxone",
          explanation: "Naloxone (Narcan) is an opioid antagonist that rapidly reverses opioid overdose by competitively binding to opioid receptors."
        },
        {
          question: "Which drug class is contraindicated in patients with asthma?",
          options: ["ACE inhibitors", "Beta blockers", "Calcium channel blockers", "Diuretics"],
          correctAnswer: "Beta blockers",
          explanation: "Non-selective beta blockers can cause bronchoconstriction by blocking beta-2 receptors in the lungs, worsening asthma symptoms."
        },
        {
          question: "What is the mechanism of action of statins?",
          options: ["Block cholesterol absorption", "Inhibit HMG-CoA reductase", "Increase bile acid excretion", "Activate lipoprotein lipase"],
          correctAnswer: "Inhibit HMG-CoA reductase",
          explanation: "Statins inhibit HMG-CoA reductase, the rate-limiting enzyme in cholesterol synthesis, thereby lowering LDL cholesterol levels."
        },
        {
          question: "Which antibiotic is known for causing tendon rupture?",
          options: ["Penicillin", "Azithromycin", "Fluoroquinolones", "Cephalosporins"],
          correctAnswer: "Fluoroquinolones",
          explanation: "Fluoroquinolones (like ciprofloxacin) carry a black box warning for tendon rupture, especially the Achilles tendon, particularly in elderly patients."
        },
        {
          question: "What is the reversal agent for heparin?",
          options: ["Vitamin K", "Protamine sulfate", "Fresh frozen plasma", "Idarucizumab"],
          correctAnswer: "Protamine sulfate",
          explanation: "Protamine sulfate binds to heparin and neutralizes its anticoagulant effect. It's derived from fish sperm and can cause allergic reactions."
        },
        {
          question: "Which drug is first-line treatment for anaphylaxis?",
          options: ["Diphenhydramine", "Epinephrine", "Hydrocortisone", "Albuterol"],
          correctAnswer: "Epinephrine",
          explanation: "Epinephrine (adrenaline) is the first-line treatment for anaphylaxis. It works quickly to reverse bronchospasm, hypotension, and angioedema."
        },
        {
          question: "What is the antidote for acetaminophen (paracetamol) overdose?",
          options: ["Naloxone", "Flumazenil", "N-acetylcysteine", "Activated charcoal"],
          correctAnswer: "N-acetylcysteine",
          explanation: "N-acetylcysteine (NAC) replenishes glutathione stores and helps detoxify the toxic metabolite NAPQI that causes liver damage in acetaminophen overdose."
        },
        {
          question: "Which class of antidepressants inhibits serotonin reuptake?",
          options: ["MAOIs", "TCAs", "SSRIs", "SNRIs"],
          correctAnswer: "SSRIs",
          explanation: "SSRIs (Selective Serotonin Reuptake Inhibitors) like fluoxetine and sertraline work by blocking the reuptake of serotonin, increasing its availability."
        },
        {
          question: "What is the mechanism of action of proton pump inhibitors (PPIs)?",
          options: ["Block H2 receptors", "Inhibit H+/K+ ATPase", "Neutralize stomach acid", "Coat the stomach lining"],
          correctAnswer: "Inhibit H+/K+ ATPase",
          explanation: "PPIs like omeprazole irreversibly inhibit the H+/K+ ATPase pump in gastric parietal cells, reducing stomach acid production by up to 99%."
        },
        {
          question: "Which medication can cause a lupus-like syndrome?",
          options: ["Amoxicillin", "Hydralazine", "Metformin", "Lisinopril"],
          correctAnswer: "Hydralazine",
          explanation: "Hydralazine is a classic cause of drug-induced lupus, along with procainamide and isoniazid. Symptoms typically resolve after stopping the drug."
        },
        {
          question: "What is the mechanism of action of benzodiazepines?",
          options: ["Block dopamine receptors", "Enhance GABA activity", "Inhibit serotonin reuptake", "Block sodium channels"],
          correctAnswer: "Enhance GABA activity",
          explanation: "Benzodiazepines enhance the effect of GABA at the GABA-A receptor, increasing chloride conductance and causing CNS depression."
        },
        {
          question: "Which drug is the antidote for benzodiazepine overdose?",
          options: ["Naloxone", "Flumazenil", "Atropine", "Physostigmine"],
          correctAnswer: "Flumazenil",
          explanation: "Flumazenil is a competitive antagonist at the benzodiazepine binding site on GABA-A receptors, reversing sedation and respiratory depression."
        },
        {
          question: "What is the primary side effect of aminoglycoside antibiotics?",
          options: ["Hepatotoxicity", "Nephrotoxicity and ototoxicity", "Cardiotoxicity", "Pulmonary fibrosis"],
          correctAnswer: "Nephrotoxicity and ototoxicity",
          explanation: "Aminoglycosides (gentamicin, tobramycin) can cause kidney damage and hearing loss. Monitoring drug levels and renal function is essential."
        },
        {
          question: "Which class of drugs causes 'first-dose hypotension'?",
          options: ["Beta blockers", "Alpha-1 blockers", "Calcium channel blockers", "Thiazide diuretics"],
          correctAnswer: "Alpha-1 blockers",
          explanation: "Alpha-1 blockers like prazosin can cause marked hypotension with the first dose. Patients should take the first dose at bedtime."
        },
        {
          question: "What is the mechanism of action of methotrexate?",
          options: ["Inhibits dihydrofolate reductase", "Alkylates DNA", "Inhibits topoisomerase", "Blocks microtubule assembly"],
          correctAnswer: "Inhibits dihydrofolate reductase",
          explanation: "Methotrexate inhibits dihydrofolate reductase, blocking folic acid metabolism and DNA synthesis. It's used for cancer and autoimmune diseases."
        },
        {
          question: "Which antibiotic is associated with QT prolongation?",
          options: ["Amoxicillin", "Azithromycin", "Doxycycline", "Cephalexin"],
          correctAnswer: "Azithromycin",
          explanation: "Azithromycin and other macrolides can prolong the QT interval, increasing risk of torsades de pointes, especially with other QT-prolonging drugs."
        },
        {
          question: "What is the antidote for digoxin toxicity?",
          options: ["Calcium gluconate", "Digoxin immune fab", "Glucagon", "Atropine"],
          correctAnswer: "Digoxin immune fab",
          explanation: "Digoxin immune fab (Digibind) consists of antibody fragments that bind digoxin, preventing it from binding to Na+/K+ ATPase."
        },
        {
          question: "Which drug class inhibits the sodium-glucose cotransporter-2 (SGLT2)?",
          options: ["Sulfonylureas", "DPP-4 inhibitors", "Gliflozins", "Thiazolidinediones"],
          correctAnswer: "Gliflozins",
          explanation: "SGLT2 inhibitors (empagliflozin, dapagliflozin) block glucose reabsorption in the kidney, lowering blood sugar and providing cardiovascular benefits."
        },
        {
          question: "What is the mechanism of action of warfarin?",
          options: ["Inhibits thrombin directly", "Inhibits vitamin K epoxide reductase", "Inhibits factor Xa", "Activates antithrombin III"],
          correctAnswer: "Inhibits vitamin K epoxide reductase",
          explanation: "Warfarin inhibits vitamin K epoxide reductase, preventing the regeneration of vitamin K needed to activate clotting factors II, VII, IX, and X."
        },
        {
          question: "Which medication is contraindicated in patients with G6PD deficiency?",
          options: ["Acetaminophen", "Primaquine", "Amoxicillin", "Metformin"],
          correctAnswer: "Primaquine",
          explanation: "Primaquine can cause severe hemolytic anemia in G6PD-deficient patients. Other drugs to avoid include sulfonamides, nitrofurantoin, and dapsone."
        },
        {
          question: "What is the most common side effect of ACE inhibitors?",
          options: ["Hyperkalaemia", "Dry cough", "Ankle oedema", "Headache"],
          correctAnswer: "Dry cough",
          explanation: "ACE inhibitors cause a persistent dry cough in 10-15% of patients due to accumulation of bradykinin in the lungs. Switching to an ARB avoids this side effect."
        },
        {
          question: "Which diuretic acts on the Loop of Henle?",
          options: ["Hydrochlorothiazide", "Spironolactone", "Furosemide", "Acetazolamide"],
          correctAnswer: "Furosemide",
          explanation: "Furosemide inhibits the Na+/K+/2Cl- cotransporter in the thick ascending limb of the Loop of Henle. It is the most potent class of diuretic, used in heart failure and fluid overload."
        },
        {
          question: "Which immunosuppressant drug inhibits calcineurin to prevent transplant rejection?",
          options: ["Azathioprine", "Mycophenolate mofetil", "Ciclosporin", "Sirolimus"],
          correctAnswer: "Ciclosporin",
          explanation: "Ciclosporin (and tacrolimus) inhibit calcineurin, preventing T-cell activation by blocking IL-2 transcription. Key side effects include nephrotoxicity and hypertension."
        },
        {
          question: "What is the mechanism of action of allopurinol?",
          options: ["Increases uric acid excretion", "Inhibits xanthine oxidase", "Alkalinises urine", "Blocks urate transporters in the kidney"],
          correctAnswer: "Inhibits xanthine oxidase",
          explanation: "Allopurinol inhibits xanthine oxidase, reducing uric acid production. It is used for chronic gout prevention and tumour lysis syndrome prophylaxis."
        },
        {
          question: "Which anti-TB drug causes peripheral neuropathy, preventable with pyridoxine?",
          options: ["Rifampicin", "Isoniazid", "Pyrazinamide", "Ethambutol"],
          correctAnswer: "Isoniazid",
          explanation: "Isoniazid (INH) causes peripheral neuropathy by competing with pyridoxine (vitamin B6). Pyridoxine supplementation is given to diabetic, malnourished, and pregnant patients."
        },
        {
          question: "What is the mechanism of action of salbutamol?",
          options: ["Alpha-1 agonist", "Beta-2 agonist", "Muscarinic antagonist", "Phosphodiesterase inhibitor"],
          correctAnswer: "Beta-2 agonist",
          explanation: "Salbutamol is a selective beta-2 adrenergic agonist that relaxes airway smooth muscle, causing bronchodilation. It is the first-line reliever inhaler for asthma."
        },
        {
          question: "Which organ requires regular monitoring when a patient takes amiodarone?",
          options: ["Kidneys only", "Thyroid, liver, and lungs", "Bone marrow", "Pancreas"],
          correctAnswer: "Thyroid, liver, and lungs",
          explanation: "Amiodarone contains iodine and can cause thyroid dysfunction (hypo- or hyperthyroidism), hepatotoxicity, and pulmonary fibrosis. TFTs, LFTs, and CXR monitoring is essential."
        },
        {
          question: "Which drug is absolutely contraindicated in pregnancy due to teratogenicity, used for rheumatoid arthritis?",
          options: ["Hydroxychloroquine", "Sulfasalazine", "Methotrexate", "Prednisolone"],
          correctAnswer: "Methotrexate",
          explanation: "Methotrexate is a potent folate antagonist and teratogen. It must be stopped ≥3 months before conception. Women must use effective contraception while taking it."
        },
        {
          question: "What class of drugs causes nephrogenic diabetes insipidus as a recognised side effect?",
          options: ["Thiazide diuretics", "Loop diuretics", "Lithium", "Beta blockers"],
          correctAnswer: "Lithium",
          explanation: "Lithium blocks the action of ADH on renal collecting duct principal cells, causing nephrogenic diabetes insipidus with polyuria and polydipsia. Lithium levels must be monitored."
        },
        {
          question: "What is the reversal agent for direct thrombin inhibitors such as dabigatran?",
          options: ["Vitamin K", "Protamine sulfate", "Idarucizumab", "Andexanet alfa"],
          correctAnswer: "Idarucizumab",
          explanation: "Idarucizumab (Praxbind) is a monoclonal antibody fragment that rapidly and completely reverses the anticoagulant effect of dabigatran. Andexanet alfa reverses factor Xa inhibitors."
        }
      ]
    },
    {
      id: 3,
      title: "Pathology Round",
      description: "Disease processes and diagnostics",
      color: "from-green-100 to-emerald-100",
      questions: [
        {
          question: "What type of cell is primarily affected in Multiple Sclerosis?",
          options: ["Neurons", "Oligodendrocytes", "Astrocytes", "Microglia"],
          correctAnswer: "Oligodendrocytes",
          explanation: "MS is an autoimmune disease where oligodendrocytes (myelin-producing cells) are attacked, leading to demyelination in the CNS."
        },
        {
          question: "Which tumor marker is elevated in prostate cancer?",
          options: ["CEA", "CA 19-9", "PSA", "AFP"],
          correctAnswer: "PSA",
          explanation: "PSA (Prostate-Specific Antigen) is elevated in prostate cancer, though it can also be elevated in benign prostatic conditions."
        },
        {
          question: "What is the hallmark finding in Alzheimer's disease?",
          options: ["Lewy bodies", "Neurofibrillary tangles", "Pick bodies", "Negri bodies"],
          correctAnswer: "Neurofibrillary tangles",
          explanation: "Alzheimer's disease is characterized by beta-amyloid plaques and neurofibrillary tangles (made of hyperphosphorylated tau protein)."
        },
        {
          question: "Which infection is the leading cause of peptic ulcers?",
          options: ["H. pylori", "E. coli", "C. difficile", "S. aureus"],
          correctAnswer: "H. pylori",
          explanation: "Helicobacter pylori infection is responsible for about 60-90% of peptic ulcers, along with NSAIDs as another major cause."
        },
        {
          question: "What is the Reed-Sternberg cell diagnostic of?",
          options: ["Burkitt lymphoma", "Hodgkin lymphoma", "Multiple myeloma", "Chronic lymphocytic leukemia"],
          correctAnswer: "Hodgkin lymphoma",
          explanation: "Reed-Sternberg cells (large, binucleated cells with prominent nucleoli) are pathognomonic for Hodgkin lymphoma."
        },
        {
          question: "Which type of necrosis is typically seen in tuberculosis?",
          options: ["Coagulative necrosis", "Liquefactive necrosis", "Caseous necrosis", "Fat necrosis"],
          correctAnswer: "Caseous necrosis",
          explanation: "Caseous (cheese-like) necrosis is characteristic of tuberculosis and other granulomatous diseases, appearing white and crumbly."
        },
        {
          question: "What is the most common type of lung cancer?",
          options: ["Small cell carcinoma", "Squamous cell carcinoma", "Adenocarcinoma", "Large cell carcinoma"],
          correctAnswer: "Adenocarcinoma",
          explanation: "Adenocarcinoma is now the most common type of lung cancer, especially in non-smokers and women. It typically occurs in peripheral lung tissue."
        },
        {
          question: "Which chromosome abnormality is associated with Down syndrome?",
          options: ["Trisomy 13", "Trisomy 18", "Trisomy 21", "Monosomy X"],
          correctAnswer: "Trisomy 21",
          explanation: "Down syndrome is caused by trisomy 21 (three copies of chromosome 21). It's the most common chromosomal cause of intellectual disability."
        },
        {
          question: "What is the Virchow's triad associated with?",
          options: ["Heart failure", "Thrombosis", "Inflammation", "Cancer metastasis"],
          correctAnswer: "Thrombosis",
          explanation: "Virchow's triad describes the three factors that contribute to thrombosis: endothelial injury, stasis of blood flow, and hypercoagulability."
        },
        {
          question: "Which autoantibody is most specific for Systemic Lupus Erythematosus?",
          options: ["ANA", "Anti-dsDNA", "Rheumatoid factor", "Anti-CCP"],
          correctAnswer: "Anti-dsDNA",
          explanation: "While ANA is sensitive for SLE, anti-double-stranded DNA (anti-dsDNA) antibodies are highly specific for SLE and correlate with disease activity."
        },
        {
          question: "What type of hypersensitivity reaction is anaphylaxis?",
          options: ["Type I", "Type II", "Type III", "Type IV"],
          correctAnswer: "Type I",
          explanation: "Anaphylaxis is a Type I (immediate) hypersensitivity reaction mediated by IgE antibodies and mast cell degranulation."
        },
        {
          question: "Which protein is deposited in the brain in Parkinson's disease?",
          options: ["Beta-amyloid", "Tau protein", "Alpha-synuclein", "Huntingtin"],
          correctAnswer: "Alpha-synuclein",
          explanation: "Alpha-synuclein aggregates form Lewy bodies, the pathological hallmark of Parkinson's disease, found in dopaminergic neurons of the substantia nigra."
        },
        {
          question: "What is the Philadelphia chromosome associated with?",
          options: ["Acute lymphoblastic leukemia", "Chronic myeloid leukemia", "Hodgkin lymphoma", "Multiple myeloma"],
          correctAnswer: "Chronic myeloid leukemia",
          explanation: "The Philadelphia chromosome is a translocation between chromosomes 9 and 22, creating the BCR-ABL fusion gene, characteristic of CML."
        },
        {
          question: "Which virus causes cervical cancer?",
          options: ["Epstein-Barr virus", "Human papillomavirus", "Hepatitis B virus", "Herpes simplex virus"],
          correctAnswer: "Human papillomavirus",
          explanation: "HPV (especially types 16 and 18) causes nearly all cervical cancers. The HPV vaccine can prevent most cervical cancers."
        },
        {
          question: "What is the classic triad of Hemolytic Uremic Syndrome (HUS)?",
          options: ["Fever, rash, arthritis", "Hemolytic anemia, thrombocytopenia, renal failure", "Jaundice, ascites, encephalopathy", "Hypotension, bradycardia, hypothermia"],
          correctAnswer: "Hemolytic anemia, thrombocytopenia, renal failure",
          explanation: "HUS is characterized by microangiopathic hemolytic anemia, thrombocytopenia, and acute kidney injury, often following E. coli O157:H7 infection."
        },
        {
          question: "What type of collagen is defective in Osteogenesis Imperfecta?",
          options: ["Type I", "Type II", "Type III", "Type IV"],
          correctAnswer: "Type I",
          explanation: "Osteogenesis imperfecta (brittle bone disease) is caused by defects in type I collagen, the main collagen in bone, skin, and tendons."
        },
        {
          question: "Which tumor marker is associated with hepatocellular carcinoma?",
          options: ["CEA", "CA 125", "AFP", "CA 19-9"],
          correctAnswer: "AFP",
          explanation: "Alpha-fetoprotein (AFP) is elevated in hepatocellular carcinoma and certain germ cell tumors. It's also elevated during normal pregnancy."
        },
        {
          question: "What is the Auer rod pathognomonic for?",
          options: ["Chronic myeloid leukemia", "Acute myeloid leukemia", "Acute lymphoblastic leukemia", "Chronic lymphocytic leukemia"],
          correctAnswer: "Acute myeloid leukemia",
          explanation: "Auer rods are crystallized aggregates of myeloperoxidase found in the cytoplasm of myeloblasts, diagnostic for acute myeloid leukemia."
        },
        {
          question: "Which disease is characterized by anti-centromere antibodies?",
          options: ["Systemic lupus erythematosus", "Limited scleroderma (CREST)", "Rheumatoid arthritis", "Sjögren syndrome"],
          correctAnswer: "Limited scleroderma (CREST)",
          explanation: "Anti-centromere antibodies are highly specific for limited cutaneous systemic sclerosis (CREST syndrome: Calcinosis, Raynaud's, Esophageal dysmotility, Sclerodactyly, Telangiectasia)."
        },
        {
          question: "What is the most common cause of infective endocarditis in IV drug users?",
          options: ["Streptococcus viridans", "Staphylococcus aureus", "Enterococcus", "HACEK organisms"],
          correctAnswer: "Staphylococcus aureus",
          explanation: "S. aureus is the most common cause of infective endocarditis in IV drug users, typically affecting the tricuspid valve."
        },
        {
          question: "Which HLA type is associated with ankylosing spondylitis?",
          options: ["HLA-DR3", "HLA-DR4", "HLA-B27", "HLA-DQ2"],
          correctAnswer: "HLA-B27",
          explanation: "HLA-B27 is strongly associated with ankylosing spondylitis and other seronegative spondyloarthropathies like reactive arthritis and psoriatic arthritis."
        },
        {
          question: "What is the most common cause of community-acquired pneumonia?",
          options: ["Staphylococcus aureus", "Haemophilus influenzae", "Streptococcus pneumoniae", "Klebsiella pneumoniae"],
          correctAnswer: "Streptococcus pneumoniae",
          explanation: "Streptococcus pneumoniae (pneumococcus) is the most common cause of community-acquired pneumonia in adults, presenting with rust-colored sputum."
        },
        {
          question: "Which antibody is most specific for celiac disease?",
          options: ["Anti-gliadin", "Anti-tissue transglutaminase (tTG)", "Anti-endomysial", "Anti-smooth muscle"],
          correctAnswer: "Anti-tissue transglutaminase (tTG)",
          explanation: "Anti-tTG IgA antibodies are highly sensitive and specific for celiac disease and are the preferred initial screening test."
        },
        {
          question: "What is the characteristic microscopic finding in sarcoidosis?",
          options: ["Caseating granulomas", "Non-caseating granulomas", "Foam cells", "Giant cell arteritis"],
          correctAnswer: "Non-caseating granulomas",
          explanation: "Sarcoidosis is characterized by non-caseating granulomas (unlike TB which has caseating granulomas). They can affect any organ, especially lungs and lymph nodes."
        },
        {
          question: "Which genetic mutation causes sickle cell disease?",
          options: ["Glutamate to valine at position 6", "Valine to glutamate at position 6", "Glutamate to lysine at position 6", "Valine to lysine at position 6"],
          correctAnswer: "Glutamate to valine at position 6",
          explanation: "Sickle cell disease is caused by a point mutation replacing glutamate with valine at position 6 of the beta-globin chain (HbS)."
        },
        {
          question: "What is the most common cause of acute pancreatitis in the UK?",
          options: ["Alcohol", "Gallstones", "Hypertriglyceridaemia", "ERCP"],
          correctAnswer: "Gallstones",
          explanation: "Gallstones account for ~50% of acute pancreatitis cases in the UK; alcohol for ~25%. Together they account for ~80% of all cases. Severity is assessed using the Glasgow or Ranson criteria."
        },
        {
          question: "What is the classic triad of Cushing's syndrome?",
          options: ["Hypertension, hypokalaemia, alkalosis", "Central obesity, striae, moon face", "Weight loss, sweating, tachycardia", "Hypotension, hyperpigmentation, hyponatraemia"],
          correctAnswer: "Central obesity, striae, moon face",
          explanation: "Excess cortisol (Cushing's) causes central obesity, purple striae, moon face, buffalo hump, hypertension, hyperglycaemia, and osteoporosis. Hyperpigmentation suggests ectopic ACTH."
        },
        {
          question: "Which pancreatic cell type is selectively destroyed in Type 1 diabetes mellitus?",
          options: ["Alpha cells", "Beta cells", "Delta cells", "Acinar cells"],
          correctAnswer: "Beta cells",
          explanation: "Type 1 DM is an autoimmune disease causing destruction of insulin-secreting beta cells in the islets of Langerhans. Associated antibodies include anti-GAD, anti-islet cell, and anti-insulin."
        },
        {
          question: "What is the most common site of colorectal cancer?",
          options: ["Caecum", "Transverse colon", "Descending colon", "Sigmoid colon and rectum"],
          correctAnswer: "Sigmoid colon and rectum",
          explanation: "About 50-60% of colorectal cancers occur in the sigmoid colon and rectum. Left-sided tumours present with change in bowel habit and rectal bleeding; right-sided cause iron deficiency anaemia."
        },
        {
          question: "What is the classic ECG finding in hyperkalaemia?",
          options: ["Prolonged QT interval", "Tall peaked T waves", "ST elevation", "Delta waves"],
          correctAnswer: "Tall peaked T waves",
          explanation: "Hyperkalaemia produces sequential ECG changes: peaked T waves → PR prolongation → widened QRS → sinusoidal pattern → VF. Tall peaked T waves are the earliest and most recognisable sign."
        },
        {
          question: "Which autoantibody is >95% specific for primary biliary cholangitis?",
          options: ["Anti-smooth muscle antibody", "Anti-mitochondrial antibody", "Anti-LKM antibody", "Anti-nuclear antibody"],
          correctAnswer: "Anti-mitochondrial antibody",
          explanation: "Anti-mitochondrial antibodies (AMA), particularly anti-M2 directed against pyruvate dehydrogenase, are present in >95% of PBC patients. PBC causes cholestasis and eventually cirrhosis."
        },
        {
          question: "What is the underlying genetic defect in Marfan syndrome?",
          options: ["Type I collagen mutation", "Fibrillin-1 (FBN1) mutation", "Elastin gene deletion", "COL3A1 mutation"],
          correctAnswer: "Fibrillin-1 (FBN1) mutation",
          explanation: "Marfan syndrome is caused by mutations in FBN1 encoding fibrillin-1. Features include tall stature, arachnodactyly, ectopia lentis, and aortic root dilatation risking dissection."
        },
        {
          question: "Which enzyme is most specific for acute myocardial infarction?",
          options: ["CK-MB", "LDH", "Troponin I/T", "AST"],
          correctAnswer: "Troponin I/T",
          explanation: "High-sensitivity troponin I and T are the most sensitive and specific biomarkers for myocardial injury. They rise within 3-4 hours of MI and remain elevated for 10-14 days."
        },
        {
          question: "What is the underlying pathology in disseminated intravascular coagulation (DIC)?",
          options: ["Isolated thrombocytopenia", "Simultaneous systemic clotting and fibrinolysis", "Vitamin K deficiency", "Factor VIII deficiency"],
          correctAnswer: "Simultaneous systemic clotting and fibrinolysis",
          explanation: "DIC involves widespread microvascular thrombosis depleting clotting factors and platelets, followed by secondary fibrinolysis. Lab findings: raised PT/APTT/D-dimer, low platelets and fibrinogen."
        },
        {
          question: "What is the most common primary bone tumour in adolescents?",
          options: ["Chondrosarcoma", "Osteosarcoma", "Ewing's sarcoma", "Giant cell tumour"],
          correctAnswer: "Osteosarcoma",
          explanation: "Osteosarcoma is the most common primary malignant bone tumour, typically arising in the metaphysis of long bones (distal femur, proximal tibia) in adolescents during rapid growth."
        }
      ]
    },
    {
      id: 4,
      title: "Physiology Round",
      description: "How the body works",
      color: "from-purple-100 to-violet-100",
      questions: [
        {
          question: "What is the normal pH range of blood?",
          options: ["6.35-6.45", "7.05-7.15", "7.35-7.45", "7.65-7.75"],
          correctAnswer: "7.35-7.45",
          explanation: "Normal arterial blood pH is tightly regulated between 7.35-7.45. Values below 7.35 indicate acidosis, above 7.45 indicate alkalosis."
        },
        {
          question: "Which hormone regulates calcium levels in blood?",
          options: ["Calcitonin", "PTH", "Vitamin D", "All of the above"],
          correctAnswer: "All of the above",
          explanation: "Calcium homeostasis involves PTH (increases calcium), calcitonin (decreases calcium), and vitamin D (increases intestinal absorption)."
        },
        {
          question: "What is the primary function of the loop of Henle?",
          options: ["Filtration", "Reabsorption", "Concentration of urine", "Secretion"],
          correctAnswer: "Concentration of urine",
          explanation: "The loop of Henle creates a concentration gradient in the kidney medulla, essential for producing concentrated urine and conserving water."
        },
        {
          question: "Which cells produce surfactant in the lungs?",
          options: ["Type I pneumocytes", "Type II pneumocytes", "Clara cells", "Alveolar macrophages"],
          correctAnswer: "Type II pneumocytes",
          explanation: "Type II pneumocytes produce surfactant, which reduces surface tension in alveoli and prevents collapse during expiration."
        },
        {
          question: "What is the pacemaker of the heart?",
          options: ["AV node", "SA node", "Bundle of His", "Purkinje fibers"],
          correctAnswer: "SA node",
          explanation: "The sinoatrial (SA) node is the heart's natural pacemaker, initiating electrical impulses at 60-100 beats per minute."
        },
        {
          question: "What is the normal Glomerular Filtration Rate (GFR)?",
          options: ["60-80 mL/min", "90-120 mL/min", "140-160 mL/min", "180-200 mL/min"],
          correctAnswer: "90-120 mL/min",
          explanation: "Normal GFR is approximately 90-120 mL/min. GFR below 60 mL/min for 3+ months indicates chronic kidney disease."
        },
        {
          question: "Which neurotransmitter is primarily responsible for muscle contraction?",
          options: ["Dopamine", "Serotonin", "Acetylcholine", "GABA"],
          correctAnswer: "Acetylcholine",
          explanation: "Acetylcholine is released at the neuromuscular junction and binds to nicotinic receptors on muscle cells to initiate contraction."
        },
        {
          question: "What is the oxygen-carrying protein in red blood cells?",
          options: ["Myoglobin", "Hemoglobin", "Albumin", "Ferritin"],
          correctAnswer: "Hemoglobin",
          explanation: "Hemoglobin is a tetrameric protein containing iron that carries oxygen from the lungs to tissues and carbon dioxide back to the lungs."
        },
        {
          question: "Which part of the nephron is impermeable to water?",
          options: ["Proximal tubule", "Descending loop of Henle", "Ascending loop of Henle", "Collecting duct"],
          correctAnswer: "Ascending loop of Henle",
          explanation: "The thick ascending limb of the loop of Henle is impermeable to water but actively transports sodium, creating the medullary concentration gradient."
        },
        {
          question: "What hormone stimulates red blood cell production?",
          options: ["Thrombopoietin", "Erythropoietin", "G-CSF", "Interleukin-3"],
          correctAnswer: "Erythropoietin",
          explanation: "Erythropoietin (EPO) is produced mainly by the kidneys in response to hypoxia and stimulates red blood cell production in the bone marrow."
        },
        {
          question: "What is the Frank-Starling law of the heart?",
          options: ["Heart rate increases with exercise", "Stroke volume increases with increased preload", "Blood pressure rises with age", "Cardiac output equals heart rate times stroke volume"],
          correctAnswer: "Stroke volume increases with increased preload",
          explanation: "The Frank-Starling law states that the heart pumps out whatever volume it receives - increased venous return (preload) leads to increased stroke volume."
        },
        {
          question: "Which ion is primarily responsible for the resting membrane potential?",
          options: ["Sodium", "Potassium", "Calcium", "Chloride"],
          correctAnswer: "Potassium",
          explanation: "The resting membrane potential (-70mV) is primarily determined by potassium ions, which flow out through leak channels down their concentration gradient."
        },
        {
          question: "What triggers the release of insulin from the pancreas?",
          options: ["Low blood glucose", "High blood glucose", "Low blood pressure", "High blood pressure"],
          correctAnswer: "High blood glucose",
          explanation: "Elevated blood glucose enters beta cells via GLUT2 transporters, triggering a cascade that leads to insulin release to lower blood sugar."
        },
        {
          question: "Which hormone is released from the posterior pituitary?",
          options: ["Growth hormone", "TSH", "ADH (vasopressin)", "ACTH"],
          correctAnswer: "ADH (vasopressin)",
          explanation: "The posterior pituitary releases ADH (antidiuretic hormone) and oxytocin. These hormones are made in the hypothalamus but stored and released from the posterior pituitary."
        },
        {
          question: "What is the primary driver of respiration?",
          options: ["Oxygen levels", "Carbon dioxide levels", "Blood pH", "Lung stretch receptors"],
          correctAnswer: "Carbon dioxide levels",
          explanation: "Central chemoreceptors in the medulla primarily respond to CO2 (via H+ from CO2 + H2O → H2CO3). Hypercapnia is the main stimulus for breathing."
        },
        {
          question: "What is the normal tidal volume in an adult?",
          options: ["150 mL", "300 mL", "500 mL", "1000 mL"],
          correctAnswer: "500 mL",
          explanation: "Normal tidal volume is approximately 500 mL (6-8 mL/kg). This is the volume of air inspired or expired during normal breathing."
        },
        {
          question: "Which enzyme converts angiotensin I to angiotensin II?",
          options: ["Renin", "ACE", "Aldosterone synthase", "Chymase"],
          correctAnswer: "ACE",
          explanation: "Angiotensin-converting enzyme (ACE), found primarily in lung capillaries, converts angiotensin I to the potent vasoconstrictor angiotensin II."
        },
        {
          question: "What is the main site of action of thiazide diuretics?",
          options: ["Proximal tubule", "Loop of Henle", "Distal convoluted tubule", "Collecting duct"],
          correctAnswer: "Distal convoluted tubule",
          explanation: "Thiazides inhibit the Na+/Cl- cotransporter in the distal convoluted tubule, promoting sodium and water excretion."
        },
        {
          question: "Which phase of the cardiac cycle has the highest oxygen consumption?",
          options: ["Atrial systole", "Isovolumetric contraction", "Ventricular ejection", "Isovolumetric relaxation"],
          correctAnswer: "Isovolumetric contraction",
          explanation: "During isovolumetric contraction, the ventricle generates maximum wall tension without ejecting blood, consuming the most oxygen."
        },
        {
          question: "What is the role of 2,3-DPG in oxygen transport?",
          options: ["Increases hemoglobin oxygen affinity", "Decreases hemoglobin oxygen affinity", "Increases oxygen solubility", "Decreases CO2 transport"],
          correctAnswer: "Decreases hemoglobin oxygen affinity",
          explanation: "2,3-DPG binds to deoxyhemoglobin and shifts the oxygen-hemoglobin dissociation curve right, promoting oxygen release to tissues."
        },
        {
          question: "Which hormone increases water reabsorption in the collecting duct?",
          options: ["Aldosterone", "ANP", "ADH (vasopressin)", "Cortisol"],
          correctAnswer: "ADH (vasopressin)",
          explanation: "ADH increases water permeability of the collecting duct by inserting aquaporin-2 channels, allowing water to be reabsorbed and concentrating urine."
        },
        {
          question: "What is the effect of increased parasympathetic activity on heart rate?",
          options: ["Increases heart rate", "Decreases heart rate", "No effect", "Irregular heart rate"],
          correctAnswer: "Decreases heart rate",
          explanation: "Parasympathetic (vagal) stimulation releases acetylcholine, which decreases heart rate by hyperpolarizing the SA node cells."
        },
        {
          question: "Which buffer system is most important in blood?",
          options: ["Phosphate buffer", "Protein buffer", "Bicarbonate buffer", "Ammonia buffer"],
          correctAnswer: "Bicarbonate buffer",
          explanation: "The bicarbonate buffer system (CO2 + H2O ⇌ H2CO3 ⇌ H+ + HCO3-) is the most important blood buffer, regulated by lungs and kidneys."
        },
        {
          question: "What triggers insulin release from beta cells?",
          options: ["Low blood glucose", "ATP-sensitive K+ channel closure", "Glucagon binding", "Sympathetic stimulation"],
          correctAnswer: "ATP-sensitive K+ channel closure",
          explanation: "High glucose → increased ATP → ATP-sensitive K+ channels close → depolarization → Ca2+ influx → insulin exocytosis."
        },
        {
          question: "What is the normal cardiac output at rest?",
          options: ["2-3 L/min", "5-6 L/min", "8-10 L/min", "12-15 L/min"],
          correctAnswer: "5-6 L/min",
          explanation: "Normal resting cardiac output is approximately 5-6 L/min (heart rate ~70 bpm × stroke volume ~70 mL). It can increase 4-5 fold during exercise."
        },
        {
          question: "What is the normal partial pressure of oxygen in arterial blood (PaO2)?",
          options: ["60-70 mmHg", "80-100 mmHg", "110-120 mmHg", "130-140 mmHg"],
          correctAnswer: "80-100 mmHg",
          explanation: "Normal PaO2 is 80-100 mmHg. PaO2 <60 mmHg defines hypoxaemia and is the threshold for long-term oxygen therapy. PaO2 falls with age and altitude."
        },
        {
          question: "Which phase of the cardiac cycle produces the first heart sound (S1)?",
          options: ["Ventricular filling", "Mitral and tricuspid valve closure", "Aortic and pulmonary valve closure", "Isovolumetric relaxation"],
          correctAnswer: "Mitral and tricuspid valve closure",
          explanation: "S1 is caused by closure of the mitral and tricuspid valves at the start of systole. S2 (aortic and pulmonary valve closure) marks the end of systole."
        },
        {
          question: "What happens to blood pH during hyperventilation?",
          options: ["Decreases (respiratory acidosis)", "Increases (respiratory alkalosis)", "No change", "Depends on bicarbonate level"],
          correctAnswer: "Increases (respiratory alkalosis)",
          explanation: "Hyperventilation blows off CO2, reducing carbonic acid and raising pH (respiratory alkalosis). This reduces ionised calcium, causing perioral tingling and carpopedal spasm (tetany)."
        },
        {
          question: "In the Bohr effect, what happens to the oxygen-haemoglobin dissociation curve in acidosis?",
          options: ["Shifts left (increased O2 affinity)", "Shifts right (decreased O2 affinity)", "No change", "Becomes linear"],
          correctAnswer: "Shifts right (decreased O2 affinity)",
          explanation: "Acidosis, hypercapnia, hyperthermia, and elevated 2,3-DPG shift the curve right — reducing O2 affinity and promoting O2 offloading to metabolically active tissues."
        },
        {
          question: "What is the primary action of aldosterone in the kidney?",
          options: ["Increases water reabsorption in loop of Henle", "Increases Na+ reabsorption in the collecting duct", "Decreases H+ secretion in the proximal tubule", "Inhibits renin release"],
          correctAnswer: "Increases Na+ reabsorption in the collecting duct",
          explanation: "Aldosterone acts on principal cells of the collecting duct to upregulate ENaC and Na+/K+-ATPase, increasing Na+ reabsorption and K+ excretion. Excess causes hypertension and hypokalaemia."
        },
        {
          question: "Which lung volumes cannot be measured by spirometry alone?",
          options: ["FEV1 and FVC", "Peak expiratory flow and FEV1", "Functional residual capacity and residual volume", "Inspiratory capacity and tidal volume"],
          correctAnswer: "Functional residual capacity and residual volume",
          explanation: "Spirometry cannot measure FRC, RV, or TLC because air remains in the lungs after maximal exhalation. These require helium dilution, nitrogen washout, or body plethysmography."
        },
        {
          question: "What governs fluid movement across capillary walls (Starling forces)?",
          options: ["Hydrostatic and oncotic pressure gradients", "Cardiac output and peripheral resistance", "Renal filtration and GFR", "Alveolar pressure and lung compliance"],
          correctAnswer: "Hydrostatic and oncotic pressure gradients",
          explanation: "Starling forces: fluid leaves capillaries where capillary hydrostatic pressure exceeds plasma oncotic pressure, and is reabsorbed where oncotic pressure dominates. Oedema forms when this balance fails."
        },
        {
          question: "Which hormone mediates the acute 'fight or flight' response?",
          options: ["Cortisol", "Aldosterone", "Adrenaline (epinephrine)", "Vasopressin"],
          correctAnswer: "Adrenaline (epinephrine)",
          explanation: "Adrenaline (epinephrine) from the adrenal medulla mediates fight-or-flight: tachycardia, bronchodilation, raised glucose, and blood flow redistribution to muscle and brain."
        },
        {
          question: "What is the Hering-Breuer reflex?",
          options: ["Peripheral chemoreceptors detecting hypoxia", "Lung stretch receptors preventing over-inflation", "Carotid sinus baroreceptors detecting blood pressure", "J-receptors detecting pulmonary congestion"],
          correctAnswer: "Lung stretch receptors preventing over-inflation",
          explanation: "The Hering-Breuer inflation reflex: when lungs are sufficiently inflated, slowly adapting stretch receptors in airway smooth muscle send vagal signals to terminate inspiration, preventing over-distension."
        },
        {
          question: "What is the normal corrected QT interval (QTc) in adults?",
          options: ["<300 ms", "<440 ms", "<560 ms", "<650 ms"],
          correctAnswer: "<440 ms",
          explanation: "QTc should be <440 ms in men and <460 ms in women. QTc >500 ms greatly increases risk of torsades de pointes. Common causes: hypokalaemia, hypomagnesaemia, and certain drugs (azithromycin, haloperidol)."
        }
      ]
    }
  ], []);

  // Rapid Fire Questions - Organized by difficulty level
  const rapidQuestionsByLevel = {
    beginner: [
      { question: "Normal body temperature in Celsius?", options: ["35°C", "37°C", "38°C", "39°C"], correctAnswer: "37°C", explanation: "Normal body temperature is approximately 37°C (98.6°F)." },
      { question: "What color is oxygenated blood?", options: ["Dark red", "Bright red", "Purple", "Blue"], correctAnswer: "Bright red", explanation: "Oxygenated blood is bright red due to oxyhemoglobin." },
      { question: "Adult resting heart rate (bpm)?", options: ["40-50", "60-100", "120-140", "150-180"], correctAnswer: "60-100", explanation: "Normal resting heart rate for adults is 60-100 beats per minute." },
      { question: "Which side of the heart pumps to lungs?", options: ["Left", "Right", "Both", "Neither"], correctAnswer: "Right", explanation: "The right side of the heart pumps deoxygenated blood to the lungs." },
      { question: "What does RBC stand for?", options: ["Red Blood Cell", "Rapid Blood Count", "Renal Blood Cell", "Right Body Circulation"], correctAnswer: "Red Blood Cell", explanation: "RBC stands for Red Blood Cell, which carries oxygen." },
      { question: "Where is the patella located?", options: ["Elbow", "Knee", "Ankle", "Wrist"], correctAnswer: "Knee", explanation: "The patella is the kneecap bone." },
      { question: "What is the medical term for thigh bone?", options: ["Tibia", "Fibula", "Femur", "Humerus"], correctAnswer: "Femur", explanation: "The femur is the thigh bone, the longest bone in the body." },
      { question: "How many lungs do humans have?", options: ["1", "2", "3", "4"], correctAnswer: "2", explanation: "Humans have two lungs - left and right." },
      { question: "What is the main function of kidneys?", options: ["Pump blood", "Filter waste", "Digest food", "Store bile"], correctAnswer: "Filter waste", explanation: "Kidneys filter waste from blood and produce urine." },
      { question: "Which vitamin is produced by sunlight?", options: ["Vitamin A", "Vitamin B", "Vitamin C", "Vitamin D"], correctAnswer: "Vitamin D", explanation: "Vitamin D is synthesized in the skin when exposed to sunlight." },
      { question: "What does CPR stand for?", options: ["Cardiac Pressure Relief", "Cardiopulmonary Resuscitation", "Cellular Pump Response", "Critical Patient Recovery"], correctAnswer: "Cardiopulmonary Resuscitation", explanation: "CPR is Cardiopulmonary Resuscitation, an emergency lifesaving procedure." },
      { question: "What is the largest artery?", options: ["Carotid", "Aorta", "Pulmonary", "Femoral"], correctAnswer: "Aorta", explanation: "The aorta is the largest artery, carrying blood from the heart." },
      { question: "Adult has how many teeth?", options: ["28", "30", "32", "36"], correctAnswer: "32", explanation: "Adults have 32 permanent teeth including wisdom teeth." },
      { question: "What is the hardest substance in body?", options: ["Bone", "Enamel", "Cartilage", "Keratin"], correctAnswer: "Enamel", explanation: "Tooth enamel is the hardest substance in the human body." },
      { question: "Where is insulin produced?", options: ["Liver", "Kidney", "Pancreas", "Spleen"], correctAnswer: "Pancreas", explanation: "Insulin is produced by beta cells in the pancreas." },
      { question: "What does BP stand for?", options: ["Body Pressure", "Blood Pressure", "Brain Pattern", "Breathing Pattern"], correctAnswer: "Blood Pressure", explanation: "BP stands for Blood Pressure, the force of blood against arteries." },
      { question: "Which organ stores bile?", options: ["Liver", "Gallbladder", "Pancreas", "Spleen"], correctAnswer: "Gallbladder", explanation: "The gallbladder stores and concentrates bile produced by the liver." },
      { question: "What is the voice box called?", options: ["Pharynx", "Larynx", "Trachea", "Bronchus"], correctAnswer: "Larynx", explanation: "The larynx, or voice box, contains the vocal cords." },
      { question: "Smallest bone location?", options: ["Finger", "Toe", "Ear", "Nose"], correctAnswer: "Ear", explanation: "The stapes in the middle ear is the smallest bone." },
      { question: "How many chambers in the heart?", options: ["2", "3", "4", "5"], correctAnswer: "4", explanation: "The heart has 4 chambers: 2 atria and 2 ventricles." },
      { question: "What does BMI stand for?", options: ["Blood Mass Indicator", "Body Mass Index", "Bone Mineral Index", "Basic Medical Information"], correctAnswer: "Body Mass Index", explanation: "BMI = weight (kg) ÷ height² (m²). A normal BMI is 18.5–24.9." },
      { question: "Which blood type is the universal recipient?", options: ["A+", "B+", "AB+", "O+"], correctAnswer: "AB+", explanation: "AB positive is the universal recipient — it can receive red cells from any ABO and Rhesus type." },
      { question: "Which organ is responsible for breaking down alcohol?", options: ["Kidney", "Liver", "Pancreas", "Spleen"], correctAnswer: "Liver", explanation: "The liver metabolises alcohol via alcohol dehydrogenase to acetaldehyde, then acetate." },
      { question: "What does MRI stand for?", options: ["Medical Radio Imaging", "Magnetic Resonance Imaging", "Multiple Radiation Index", "Medial Rib Investigation"], correctAnswer: "Magnetic Resonance Imaging", explanation: "MRI uses magnetic fields and radiofrequency pulses to create detailed soft-tissue images without ionising radiation." },
      { question: "What are platelets primarily responsible for?", options: ["Carrying oxygen", "Fighting infection", "Blood clotting", "Producing antibodies"], correctAnswer: "Blood clotting", explanation: "Platelets (thrombocytes) are cell fragments that aggregate at sites of vascular injury, forming the primary plug and activating the coagulation cascade." },
      { question: "Which gland produces digestive enzymes and also controls blood sugar?", options: ["Salivary gland", "Liver", "Pancreas", "Gallbladder"], correctAnswer: "Pancreas", explanation: "The exocrine pancreas produces digestive enzymes (amylase, lipase, proteases); the endocrine pancreas produces insulin and glucagon." },
      { question: "What is the medical term for high blood pressure?", options: ["Hypotension", "Hypertension", "Tachycardia", "Bradycardia"], correctAnswer: "Hypertension", explanation: "Hypertension is defined as persistent blood pressure ≥140/90 mmHg. It is the leading modifiable risk factor for stroke and heart disease." },
      { question: "What does CT scan stand for?", options: ["Cellular Tissue scan", "Computed Tomography", "Cardiovascular Test", "Contrast Transfer"], correctAnswer: "Computed Tomography", explanation: "CT (Computed Tomography) uses X-rays from multiple angles to produce cross-sectional images of the body." },
      { question: "What is tachycardia defined as?", options: ["Heart rate < 60 bpm", "Heart rate > 100 bpm", "Heart rate > 150 bpm", "Irregular heart rhythm"], correctAnswer: "Heart rate > 100 bpm", explanation: "Tachycardia is a resting heart rate above 100 bpm. Bradycardia is below 60 bpm. Both can be physiological or pathological." },
      { question: "What colour is deoxygenated blood?", options: ["Bright red", "Blue", "Dark red", "Purple"], correctAnswer: "Dark red", explanation: "Deoxygenated blood is dark red (not blue — that's a myth). It appears blue through skin due to the way light penetrates tissue differently." },
    ],
    intermediate: [
      { question: "What does ECG measure?", options: ["Brain waves", "Heart electrical activity", "Lung capacity", "Blood sugar"], correctAnswer: "Heart electrical activity", explanation: "ECG (electrocardiogram) measures the heart's electrical activity." },
      { question: "Normal blood glucose range (mg/dL)?", options: ["40-60", "70-100", "120-140", "160-180"], correctAnswer: "70-100", explanation: "Normal fasting blood glucose is 70-100 mg/dL." },
      { question: "What does WBC stand for?", options: ["White Blood Cell", "Whole Body Count", "Water Balance Control", "Weight Base Calculation"], correctAnswer: "White Blood Cell", explanation: "WBC stands for White Blood Cell, which fights infection." },
      { question: "Which organ produces bile?", options: ["Gallbladder", "Liver", "Pancreas", "Stomach"], correctAnswer: "Liver", explanation: "The liver produces bile; the gallbladder only stores it." },
      { question: "Normal systolic blood pressure?", options: ["80-90", "100-120", "140-160", "180-200"], correctAnswer: "100-120", explanation: "Normal systolic blood pressure is less than 120 mmHg." },
      { question: "What is the windpipe called?", options: ["Esophagus", "Trachea", "Larynx", "Pharynx"], correctAnswer: "Trachea", explanation: "The trachea is the windpipe that carries air to the lungs." },
      { question: "Where are red blood cells made?", options: ["Liver", "Spleen", "Bone marrow", "Heart"], correctAnswer: "Bone marrow", explanation: "Red blood cells are produced in the bone marrow." },
      { question: "What does IV stand for?", options: ["Internal Valve", "Intravenous", "Invasive Ventilation", "Immediate Vital"], correctAnswer: "Intravenous", explanation: "IV stands for Intravenous, meaning into a vein." },
      { question: "What carries blood away from heart?", options: ["Veins", "Arteries", "Capillaries", "Lymphatics"], correctAnswer: "Arteries", explanation: "Arteries carry blood away from the heart." },
      { question: "Normal respiratory rate (breaths/min)?", options: ["6-8", "12-20", "25-30", "35-40"], correctAnswer: "12-20", explanation: "Normal adult respiratory rate is 12-20 breaths per minute." },
      { question: "What is the collarbone called?", options: ["Scapula", "Sternum", "Clavicle", "Humerus"], correctAnswer: "Clavicle", explanation: "The clavicle is the collarbone." },
      { question: "Which blood type is universal donor?", options: ["A", "B", "AB", "O"], correctAnswer: "O", explanation: "Type O negative is the universal donor blood type." },
      { question: "Where is the appendix located?", options: ["Left upper", "Right upper", "Left lower", "Right lower"], correctAnswer: "Right lower", explanation: "The appendix is in the right lower quadrant of the abdomen." },
      { question: "Largest organ inside the body?", options: ["Brain", "Heart", "Liver", "Lungs"], correctAnswer: "Liver", explanation: "The liver is the largest internal organ." },
      { question: "Normal oxygen saturation?", options: ["85-90%", "95-100%", "75-80%", "65-70%"], correctAnswer: "95-100%", explanation: "Normal oxygen saturation (SpO2) is 95-100%." },
      { question: "What is the breastbone called?", options: ["Clavicle", "Scapula", "Sternum", "Rib"], correctAnswer: "Sternum", explanation: "The sternum is the breastbone in the center of the chest." },
      { question: "How many pairs of ribs?", options: ["10", "12", "14", "16"], correctAnswer: "12", explanation: "Humans have 12 pairs of ribs (24 total)." },
      { question: "Which vitamin prevents scurvy?", options: ["Vitamin A", "Vitamin B", "Vitamin C", "Vitamin D"], correctAnswer: "Vitamin C", explanation: "Vitamin C deficiency causes scurvy." },
      { question: "What is the funny bone nerve?", options: ["Radial", "Median", "Ulnar", "Axillary"], correctAnswer: "Ulnar", explanation: "The ulnar nerve causes that 'funny bone' sensation." },
      { question: "What carries blood to the heart?", options: ["Arteries", "Veins", "Capillaries", "Lymph vessels"], correctAnswer: "Veins", explanation: "Veins carry blood back to the heart." },
      { question: "What does ICU stand for?", options: ["Internal Care Unit", "Intensive Care Unit", "Immediate Clinical Use", "Integrated Cardiology Unit"], correctAnswer: "Intensive Care Unit", explanation: "The ICU (Intensive Care Unit) manages critically ill patients requiring close monitoring and organ support." },
      { question: "Which vitamin deficiency causes rickets in children?", options: ["Vitamin A", "Vitamin B12", "Vitamin C", "Vitamin D"], correctAnswer: "Vitamin D", explanation: "Vitamin D deficiency impairs calcium absorption, leading to rickets (bowed legs, craniotabes) in children and osteomalacia in adults." },
      { question: "Normal platelet count range (×10⁹/L)?", options: ["50–100", "150–400", "500–700", "800–1000"], correctAnswer: "150–400", explanation: "Normal platelet count is 150–400 ×10⁹/L. <150 is thrombocytopenia; >450 is thrombocytosis." },
      { question: "Which gland is often called the 'master gland'?", options: ["Thyroid", "Adrenal", "Pituitary", "Thymus"], correctAnswer: "Pituitary", explanation: "The pituitary gland controls other endocrine glands via trophic hormones (TSH, ACTH, FSH, LH, GH, PRL). It sits in the sella turcica at the base of the brain." },
      { question: "What is the medical term for elevated white cell count?", options: ["Leukopaenia", "Leukocytosis", "Polycythaemia", "Thrombocytosis"], correctAnswer: "Leukocytosis", explanation: "Leukocytosis (WBC >11 ×10⁹/L) occurs in infection, inflammation, stress, and malignancy. Leukopaenia is a low WBC." },
      { question: "Which blood test is the best marker of kidney function?", options: ["ALT", "Bilirubin", "Creatinine", "Albumin"], correctAnswer: "Creatinine", explanation: "Serum creatinine reflects GFR (glomerular filtration rate). eGFR is calculated from creatinine, age, and sex. Urea is also elevated in renal failure but less specific." },
      { question: "What is a normal BMI range for an adult?", options: ["15.0–17.4", "18.5–24.9", "25.0–29.9", "30.0–34.9"], correctAnswer: "18.5–24.9", explanation: "BMI 18.5–24.9 is normal. <18.5 underweight; 25–29.9 overweight; ≥30 obese. BMI has limitations and doesn't distinguish muscle from fat." },
      { question: "What does DVT stand for?", options: ["Deep Venous Test", "Deep Vein Thrombosis", "Distal Vascular Treatment", "Double Vessel Thromboembolism"], correctAnswer: "Deep Vein Thrombosis", explanation: "DVT is a blood clot in a deep vein, usually the leg. The main risk is pulmonary embolism if the clot dislodges." },
      { question: "What is the medical term for low blood pressure?", options: ["Hypertension", "Hypotension", "Bradycardia", "Tachypnoea"], correctAnswer: "Hypotension", explanation: "Hypotension is blood pressure below 90/60 mmHg. Orthostatic (postural) hypotension is a drop ≥20/10 mmHg on standing." },
      { question: "Which vitamin is important for blood clotting?", options: ["Vitamin A", "Vitamin B1", "Vitamin C", "Vitamin K"], correctAnswer: "Vitamin K", explanation: "Vitamin K is essential for synthesis of clotting factors II, VII, IX, and X. Warfarin works by inhibiting vitamin K epoxide reductase." },
    ],
    advanced: [
      { question: "What does DNR stand for?", options: ["Do Not Resuscitate", "Daily Nursing Report", "Direct Neural Response", "Dose Not Required"], correctAnswer: "Do Not Resuscitate", explanation: "DNR is a medical order to not perform CPR." },
      { question: "What does STAT mean?", options: ["Standard Testing", "Immediately", "Scheduled Treatment", "Special Technique"], correctAnswer: "Immediately", explanation: "STAT means immediately, from Latin 'statim'." },
      { question: "What does PRN mean?", options: ["Primary Response Needed", "As Needed", "Patient Record Number", "Pre-Registration Notice"], correctAnswer: "As Needed", explanation: "PRN means 'as needed', from Latin 'pro re nata'." },
      { question: "What does NPO mean?", options: ["No Pain Observed", "Nothing By Mouth", "Normal Patient Observation", "New Patient Orders"], correctAnswer: "Nothing By Mouth", explanation: "NPO means nothing by mouth before surgery/procedures." },
      { question: "What does CBC test for?", options: ["Heart function", "Blood cells", "Kidney function", "Liver enzymes"], correctAnswer: "Blood cells", explanation: "CBC (Complete Blood Count) counts different blood cells." },
      { question: "Which gland controls metabolism?", options: ["Pituitary", "Thyroid", "Adrenal", "Pancreas"], correctAnswer: "Thyroid", explanation: "The thyroid gland regulates metabolism." },
      { question: "What is the heel bone called?", options: ["Talus", "Calcaneus", "Metatarsal", "Navicular"], correctAnswer: "Calcaneus", explanation: "The calcaneus is the heel bone." },
      { question: "Largest vein in the body?", options: ["Jugular", "Femoral", "Vena cava", "Portal"], correctAnswer: "Vena cava", explanation: "The vena cava is the largest vein, returning blood to the heart." },
      { question: "What neurotransmitter causes muscle contraction?", options: ["Dopamine", "Serotonin", "Acetylcholine", "GABA"], correctAnswer: "Acetylcholine", explanation: "Acetylcholine is released at neuromuscular junctions." },
      { question: "Normal GFR range (mL/min)?", options: ["60-80", "90-120", "140-160", "180-200"], correctAnswer: "90-120", explanation: "Normal GFR is 90-120 mL/min, indicating healthy kidney function." },
      { question: "What is the antidote for opioid overdose?", options: ["Flumazenil", "Naloxone", "Atropine", "Epinephrine"], correctAnswer: "Naloxone", explanation: "Naloxone (Narcan) reverses opioid overdose by blocking opioid receptors." },
      { question: "Which electrolyte affects heart rhythm most?", options: ["Sodium", "Potassium", "Calcium", "Chloride"], correctAnswer: "Potassium", explanation: "Potassium imbalances can cause fatal arrhythmias." },
      { question: "What type of shock is anaphylaxis?", options: ["Cardiogenic", "Distributive", "Hypovolemic", "Obstructive"], correctAnswer: "Distributive", explanation: "Anaphylaxis causes distributive shock due to massive vasodilation." },
      { question: "Normal PT/INR for warfarin therapy?", options: ["1.0-1.5", "2.0-3.0", "3.5-4.5", "5.0-6.0"], correctAnswer: "2.0-3.0", explanation: "Therapeutic INR for most warfarin indications is 2.0-3.0." },
      { question: "What does BUN measure?", options: ["Liver function", "Kidney function", "Thyroid function", "Pancreas function"], correctAnswer: "Kidney function", explanation: "BUN (Blood Urea Nitrogen) reflects kidney function." },
      { question: "First-line drug for anaphylaxis?", options: ["Diphenhydramine", "Epinephrine", "Hydrocortisone", "Albuterol"], correctAnswer: "Epinephrine", explanation: "Epinephrine IM is the first-line treatment for anaphylaxis." },
      { question: "What does QRS complex represent?", options: ["Atrial depolarization", "Ventricular depolarization", "Atrial repolarization", "Ventricular repolarization"], correctAnswer: "Ventricular depolarization", explanation: "QRS represents ventricular depolarization (contraction)." },
      { question: "Normal serum sodium range (mEq/L)?", options: ["125-130", "135-145", "150-160", "165-175"], correctAnswer: "135-145", explanation: "Normal serum sodium is 135-145 mEq/L." },
      { question: "What does troponin indicate?", options: ["Liver damage", "Cardiac muscle damage", "Kidney damage", "Brain damage"], correctAnswer: "Cardiac muscle damage", explanation: "Elevated troponin indicates myocardial (heart muscle) injury." },
      { question: "Which drug reverses benzodiazepines?", options: ["Naloxone", "Flumazenil", "Atropine", "Protamine"], correctAnswer: "Flumazenil", explanation: "Flumazenil is the specific antidote for benzodiazepine overdose." },
      { question: "What does COPD stand for?", options: ["Cardiac Output Pressure Disorder", "Chronic Obstructive Pulmonary Disease", "Chronic Oxygen Perfusion Deficit", "Congenital Obstructive Pulmonary Defect"], correctAnswer: "Chronic Obstructive Pulmonary Disease", explanation: "COPD is characterised by persistent airflow limitation, primarily from emphysema and chronic bronchitis, usually caused by smoking." },
      { question: "Which ion is most abundant inside cells (intracellular)?", options: ["Sodium", "Potassium", "Calcium", "Chloride"], correctAnswer: "Potassium", explanation: "Intracellular K+ is ~140 mEq/L; extracellular Na+ is ~140 mEq/L. This gradient, maintained by Na+/K+-ATPase, generates the resting membrane potential." },
      { question: "Which nerve is damaged in wrist drop?", options: ["Median nerve", "Ulnar nerve", "Radial nerve", "Musculocutaneous nerve"], correctAnswer: "Radial nerve", explanation: "Radial nerve palsy causes wrist drop (inability to extend wrist). Common causes: 'Saturday night palsy' (compression against humerus), mid-shaft humeral fracture." },
      { question: "What does the AST blood test primarily measure?", options: ["Kidney function", "Thyroid function", "Hepatocellular damage", "Cardiac output"], correctAnswer: "Hepatocellular damage", explanation: "AST (aspartate aminotransferase) is released from hepatocytes when damaged. It is also elevated in muscle damage. ALT is more liver-specific." },
      { question: "What is the normal serum calcium range (mmol/L)?", options: ["1.0–1.5", "2.2–2.6", "3.0–3.5", "4.0–4.5"], correctAnswer: "2.2–2.6", explanation: "Normal serum calcium is 2.2–2.6 mmol/L. Hypocalcaemia causes tetany and Chvostek's sign; hypercalcaemia causes 'bones, stones, groans, and psychic moans'." },
      { question: "Which immunoglobulin mediates immediate (Type I) hypersensitivity reactions?", options: ["IgA", "IgG", "IgM", "IgE"], correctAnswer: "IgE", explanation: "IgE binds to mast cells and basophils. Re-exposure to antigen cross-links IgE, triggering degranulation and release of histamine, causing allergic reactions and anaphylaxis." },
      { question: "What p-value threshold conventionally indicates statistical significance?", options: ["<0.10", "<0.05", "<0.01", "<0.001"], correctAnswer: "<0.05", explanation: "By convention, p<0.05 is the threshold for statistical significance, meaning there is less than a 5% probability that the result occurred by chance if the null hypothesis is true." },
      { question: "Which coagulation pathway does the PT/INR test?", options: ["Intrinsic pathway", "Extrinsic pathway", "Common pathway only", "Fibrinolytic pathway"], correctAnswer: "Extrinsic pathway", explanation: "PT/INR tests the extrinsic pathway (factor VII + tissue factor) and common pathway (X, V, II, fibrinogen). APTT tests the intrinsic pathway (XII, XI, IX, VIII)." },
      { question: "What does FEV1 stand for?", options: ["Forced Expiratory Volume in 10 seconds", "Forced Expiratory Volume in 1 second", "Full Expiratory Ventilation at level 1", "Final Expiratory Value 1"], correctAnswer: "Forced Expiratory Volume in 1 second", explanation: "FEV1 is the volume exhaled in the first second of a forced expiration from full inspiration. FEV1/FVC ratio <0.7 indicates obstruction (COPD, asthma)." },
      { question: "Which HLA type is strongly associated with narcolepsy?", options: ["HLA-B27", "HLA-DQ6 (DQB1*06:02)", "HLA-DR3", "HLA-DR4"], correctAnswer: "HLA-DQ6 (DQB1*06:02)", explanation: "Narcolepsy with cataplexy is strongly associated with HLA-DQB1*06:02, and is thought to result from autoimmune destruction of hypocretin (orexin)-producing neurons in the hypothalamus." },
    ],
  };

  // Get current rapid questions based on selected level
  const rapidQuestions = selectedLevel ? rapidQuestionsByLevel[selectedLevel] : [];

  const getLevelRewards = useCallback(() => {
    if (selectedMode !== "rapid") return { coins: 5, xp: 10 };
    switch (selectedLevel) {
      case "beginner": return { coins: 8, xp: 15 };
      case "intermediate": return { coins: 10, xp: 20 };
      case "advanced": return { coins: 12, xp: 25 };
      default: return { coins: 8, xp: 15 };
    }
  }, [selectedMode, selectedLevel]);

  // Timer effect for rapid mode
  useEffect(() => {
    let interval;
    if (timerActive && timeLeft > 0 && !showResult) {
      interval = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
    } else if (timeLeft === 0 && !showResult && selectedMode === "rapid") {
      // Time's up - auto submit
      handleSubmit();
    }

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timerActive, timeLeft, showResult]);

  // Track game completion when game finishes (runs at component level, not conditionally)
  useEffect(() => {
    if (gameMode === "complete" && !hasTrackedCompletion) {
      const levelRewards = getLevelRewards();
      const coinsPerQuestion = levelRewards.coins;
      const xpPerQuestion = levelRewards.xp;
      const levelName = selectedLevel ? ` (${selectedLevel.charAt(0).toUpperCase() + selectedLevel.slice(1)})` : "";
      const gameName = selectedMode === "rapid" ? `Pub Quiz - Rapid Fire${levelName}` : "Pub Quiz - Classic";

      trackGameComplete(gameName, score, score * coinsPerQuestion, score * xpPerQuestion);
      setHasTrackedCompletion(true);
    }
  }, [gameMode, hasTrackedCompletion, selectedMode, score, getLevelRewards, selectedLevel]);

  const handleModeSelect = (mode) => {
    setSelectedMode(mode);
    if (mode === "classic") {
      setGameMode("playing");
      setCurrentRound(0);
      setCurrentQuestion(0);
      setScore(0);
      setRoundScores([]);
      setRoundAnswers([]);
      setSelectedAnswer(null);
      setShowResult(false);
      setTimerActive(false);
    } else if (mode === "rapid") {
      // Go to level selection first for rapid mode
      setGameMode("levelSelect");
    }
  };

  const handleLevelSelect = (level) => {
    setSelectedLevel(level);
    setGameMode("rapid");
    setCurrentQuestion(0);
    setScore(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setTimeLeft(15);
    setTimerActive(true);
  };

  const handleAnswerSelect = (answer) => {
    if (!showResult) {
      setSelectedAnswer(answer);
    }
  };

  const handleSubmit = () => {
    setShowResult(true);
    setTimerActive(false);

    const currentQ = selectedMode === "rapid"
      ? rapidQuestions[currentQuestion]
      : quizRounds[currentRound].questions[currentQuestion];

    const isCorrect = selectedAnswer === currentQ.correctAnswer;

    if (isCorrect) {
      setScore(score + 1);
      const { coins, xp } = getLevelRewards();

      const newUserData = {
        ...userData,
        coins: userData.coins + coins,
        xp: userData.xp + xp,
      };
      saveUserData(newUserData);
    }

    // Track answer for round review (classic mode only)
    if (selectedMode === "classic") {
      setRoundAnswers(prev => [...prev, {
        question: currentQ.question,
        options: currentQ.options,
        selectedAnswer,
        correctAnswer: currentQ.correctAnswer,
        explanation: currentQ.explanation,
        isCorrect,
      }]);
    }
  };

  const handleNext = () => {
    if (selectedMode === "rapid") {
      // Rapid mode logic
      if (currentQuestion + 1 < rapidQuestions.length) {
        setCurrentQuestion(currentQuestion + 1);
        setSelectedAnswer(null);
        setShowResult(false);
        setTimeLeft(15);
        setTimerActive(true);
      } else {
        // All rapid questions complete
        saveUserData({ ...userData, avatarStats: applyBoost(userData.avatarStats, GAME_BOOST) });
        setGameMode("complete");
      }
    } else {
      // Classic mode logic
      const round = quizRounds[currentRound];

      if (currentQuestion + 1 < round.questions.length) {
        // Move to next question in same round
        setCurrentQuestion(currentQuestion + 1);
        setSelectedAnswer(null);
        setShowResult(false);
      } else {
        // Round complete — record round score then show review screen
        const currentQ = round.questions[currentQuestion];
        const isCorrect = selectedAnswer === currentQ.correctAnswer;
        const roundScore = score + (isCorrect ? 1 : 0);

        setRoundScores([...roundScores, { round: currentRound, score: roundScore - roundScores.reduce((sum, r) => sum + r.score, 0) }]);
        setGameMode("roundComplete");
      }
    }
  };

  const handleRestart = () => {
    setGameMode("playing");
    setCurrentRound(0);
    setCurrentQuestion(0);
    setHasTrackedCompletion(false);
    setScore(0);
    setRoundScores([]);
    setRoundAnswers([]);
    setSelectedAnswer(null);
    setShowResult(false);
  };

  const handleBackToSelection = () => {
    setGameMode("selection");
    setCurrentRound(0);
    setCurrentQuestion(0);
    setScore(0);
    setRoundScores([]);
    setRoundAnswers([]);
    setSelectedAnswer(null);
    setShowResult(false);
    setHasTrackedCompletion(false);
  };

  const handleNextRound = () => {
    setRoundAnswers([]);
    if (currentRound + 1 < quizRounds.length) {
      setCurrentRound(currentRound + 1);
      setCurrentQuestion(0);
      setSelectedAnswer(null);
      setShowResult(false);
      setGameMode("playing");
    } else {
      saveUserData({ ...userData, avatarStats: applyBoost(userData.avatarStats, GAME_BOOST) });
      setGameMode("complete");
    }
  };

  // Mode Selection Screen
  if (gameMode === "selection") {
    return (
      <div
        className="min-h-screen"
        style={{
          backgroundImage: "url('/avatar-pubquiz.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="min-h-screen bg-gradient-to-br from-purple-900/80 to-violet-900/80">
          <div className="max-w-5xl mx-auto px-4 py-8">
            <button
              onClick={onBack}
              className="mb-4 text-white hover:text-gray-200 flex items-center gap-2"
            >
              ← Back to Games
            </button>

        <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                Pub Quiz
              </h1>
              <p className="text-gray-600">
                Choose your game mode
              </p>
            </div>
            <div className="w-16 h-16">
              <img
                src="/avatar-pubquiz.png"
                alt="Pub Quiz"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-white mb-6">Select Mode</h2>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Classic Quiz Mode */}
          <button
            onClick={() => handleModeSelect("classic")}
            className="rounded-xl p-6 border-2 text-left transition-all transform hover:shadow-xl hover:scale-105 bg-gradient-to-br from-purple-100 to-violet-100 border-purple-300"
          >
            <div className="flex justify-center mb-4">
              <div className="bg-white bg-opacity-80 rounded-full p-4 shadow-md">
                <Brain className="w-8 h-8 text-purple-600" />
              </div>
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2 text-center">
              Classic Quiz
            </h3>
            <p className="text-sm text-gray-600 text-center mb-4">
              4 rounds of medical trivia with 5 questions each
            </p>
            <div className="flex items-center justify-center gap-4 text-sm">
              <span className="font-semibold text-teal-600">5 coins/question</span>
              <span className="font-semibold text-purple-600">10 XP/question</span>
            </div>
          </button>

          {/* Rapid Fire Mode */}
          <button
            onClick={() => handleModeSelect("rapid")}
            className="rounded-xl p-6 border-2 text-left transition-all transform hover:shadow-xl hover:scale-105 bg-gradient-to-br from-orange-100 to-yellow-100 border-orange-300"
          >
            <div className="flex justify-center mb-4">
              <div className="bg-white bg-opacity-80 rounded-full p-4 shadow-md">
                <Zap className="w-8 h-8 text-orange-600" />
              </div>
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2 text-center">
              Rapid Fire
            </h3>
            <p className="text-sm text-gray-600 text-center mb-4">
              20 quick questions with 15 second time limit each
            </p>
            <div className="flex items-center justify-center gap-4 text-sm">
              <span className="font-semibold text-teal-600">8 coins/question</span>
              <span className="font-semibold text-purple-600">15 XP/question</span>
            </div>
          </button>

          {/* Team Format - Coming Soon */}
          <div className="rounded-xl p-6 border-2 bg-gradient-to-br from-gray-100 to-gray-200 border-gray-300 opacity-60 relative">
            <div className="absolute top-4 right-4">
              <span className="px-3 py-1 bg-yellow-500 text-white rounded-full text-xs font-semibold">
                Coming Soon
              </span>
            </div>
            <div className="flex justify-center mb-4">
              <div className="bg-white bg-opacity-80 rounded-full p-4 shadow-md">
                <Users className="w-8 h-8 text-gray-600" />
              </div>
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2 text-center">
              Team Format
            </h3>
            <p className="text-sm text-gray-600 text-center mb-4">
              Compete against AI teams in a quiz competition
            </p>
            <div className="text-center text-sm text-gray-500">
              Feature in development
            </div>
          </div>
        </div>
          </div>
        </div>
      </div>
    );
  }

  // Level Selection Screen for Rapid Fire
  if (gameMode === "levelSelect") {
    const levels = [
      {
        id: "beginner",
        name: "Beginner",
        description: "Basic medical facts and terminology",
        color: "from-green-100 to-emerald-100",
        borderColor: "border-green-300",
        coins: 8,
        xp: 15,
        questions: 20,
      },
      {
        id: "intermediate",
        name: "Intermediate",
        description: "Clinical knowledge and normal values",
        color: "from-yellow-100 to-orange-100",
        borderColor: "border-yellow-300",
        coins: 10,
        xp: 20,
        questions: 20,
      },
      {
        id: "advanced",
        name: "Advanced",
        description: "Complex clinical reasoning and pharmacology",
        color: "from-red-100 to-pink-100",
        borderColor: "border-red-300",
        coins: 12,
        xp: 25,
        questions: 20,
      },
    ];

    return (
      <div
        className="min-h-screen"
        style={{
          backgroundImage: "url('/avatar-pubquiz.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="min-h-screen bg-gradient-to-br from-orange-900/80 to-yellow-900/80">
          <div className="max-w-5xl mx-auto px-4 py-8">
            <button
              onClick={handleBackToSelection}
              className="mb-4 text-white hover:text-gray-200 flex items-center gap-2"
            >
              ← Back to Mode Selection
            </button>

            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 mb-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h1 className="text-3xl font-bold text-gray-800 mb-2 flex items-center gap-3">
                    <Zap className="w-8 h-8 text-orange-600" />
                    Rapid Fire
                  </h1>
                  <p className="text-gray-600">
                    Choose your difficulty level
                  </p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-white mb-6">Select Level</h2>

            <div className="grid md:grid-cols-3 gap-6">
              {levels.map((level) => (
                <button
                  key={level.id}
                  onClick={() => handleLevelSelect(level.id)}
                  className={`rounded-xl p-6 border-2 text-left transition-all transform hover:shadow-xl hover:scale-105 bg-gradient-to-br ${level.color} ${level.borderColor}`}
                >
                  <h3 className="text-xl font-bold text-gray-800 mb-2 text-center">
                    {level.name}
                  </h3>
                  <p className="text-sm text-gray-600 text-center mb-4">
                    {level.description}
                  </p>
                  <div className="text-center text-xs text-gray-500 mb-3">
                    {level.questions} questions • 15 sec each
                  </div>
                  <div className="flex items-center justify-center gap-4 text-sm">
                    <span className="font-semibold text-teal-600">{level.coins} coins/q</span>
                    <span className="font-semibold text-purple-600">{level.xp} XP/q</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Game Complete Screen
  if (gameMode === "complete") {
    const totalQuestions = selectedMode === "rapid"
      ? rapidQuestions.length
      : quizRounds.reduce((sum, round) => sum + round.questions.length, 0);
    const percentage = Math.round((score / totalQuestions) * 100);
    const { coins: coinsPerQuestion, xp: xpPerQuestion } = getLevelRewards();
    const levelName = selectedLevel ? selectedLevel.charAt(0).toUpperCase() + selectedLevel.slice(1) : "";

    return (
      <div
        className="min-h-screen"
        style={{
          backgroundImage: "url('/avatar-pubquiz.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="min-h-screen bg-gradient-to-br from-purple-900/80 to-violet-900/80">
          <div className="max-w-4xl mx-auto px-4 py-8">
            <button
              onClick={onBack}
              className="mb-4 text-white hover:text-gray-200 flex items-center gap-2"
            >
              ← Back to Games
            </button>

        <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 text-center">
          <Award className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-gray-800 mb-2">
            {selectedMode === "rapid" ? `Rapid Fire (${levelName}) Complete!` : "Quiz Complete!"}
          </h2>
          <p className="text-gray-600 mb-6">
            {selectedMode === "rapid"
              ? "You've completed the rapid fire challenge!"
              : "You've finished all 4 rounds of the Pub Quiz"}
          </p>

          <div className={"bg-gradient-to-r rounded-xl p-6 mb-6 " + (selectedMode === "rapid" ? "from-orange-50 to-yellow-50" : "from-purple-50 to-violet-50")}>
            <div className={"text-5xl font-bold mb-2 " + (selectedMode === "rapid" ? "text-orange-600" : "text-purple-600")}>
              {score}/{totalQuestions}
            </div>
            <div className="text-gray-700 text-lg mb-2">
              {percentage}% Correct
            </div>
            <div className="text-sm text-gray-600">
              You earned {score * coinsPerQuestion} coins and {score * xpPerQuestion} XP!
            </div>
          </div>

          {/* Round by Round Breakdown - Only for Classic Mode */}
          {selectedMode === "classic" && (
            <div className="mb-6">
              <h3 className="text-lg font-bold text-gray-800 mb-3">Round Breakdown</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {quizRounds.map((round, index) => {
                  const roundScore = roundScores[index]?.score || 0;
                  return (
                    <div key={round.id} className={"rounded-lg p-3 bg-gradient-to-br " + round.color}>
                      <div className="text-xs text-gray-600 mb-1">{round.title}</div>
                      <div className="text-2xl font-bold text-gray-800">
                        {roundScore}/{round.questions.length}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={handleRestart}
              className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
            >
              <RotateCcw className="w-5 h-5" />
              Play Again
            </button>
            <button
              onClick={handleBackToSelection}
              className="w-full bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors"
            >
              Back to Mode Selection
            </button>
            <button
              onClick={onBack}
              className="w-full bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
            >
              Back to Games
            </button>
          </div>
        </div>
          </div>
        </div>
      </div>
    );
  }

  // Rapid Fire Mode Playing Phase
  if (gameMode === "rapid") {
    const question = rapidQuestions[currentQuestion];
    const isCorrect = showResult && selectedAnswer === question.correctAnswer;
    const totalQuestions = rapidQuestions.length;
    const progress = ((currentQuestion + 1) / totalQuestions) * 100;
    const timePercentage = (timeLeft / 15) * 100;

    // Color based on time left
    let timerColor = "bg-green-500";
    if (timeLeft <= 5) timerColor = "bg-red-500";
    else if (timeLeft <= 10) timerColor = "bg-yellow-500";

    return (
      <div
        className="min-h-screen"
        style={{
          backgroundImage: "url('/avatar-pubquiz.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="min-h-screen bg-gradient-to-br from-orange-900/80 to-yellow-900/80">
          <div className="max-w-4xl mx-auto px-4 py-8">
            <button
              onClick={handleBackToSelection}
              className="mb-4 text-white hover:text-gray-200 flex items-center gap-2"
            >
              ← Back to Mode Selection
            </button>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                    <Zap className="w-6 h-6 text-orange-600" />
                    Rapid Fire - {selectedLevel ? selectedLevel.charAt(0).toUpperCase() + selectedLevel.slice(1) : ""}
                  </h1>
                  <p className="text-sm text-gray-600">Quick thinking required!</p>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-600">
                    Question {currentQuestion + 1} of {totalQuestions}
                  </div>
                  <div className="text-lg font-bold text-orange-600">
                    Score: {score}/{currentQuestion + (showResult ? 1 : 0)}
                  </div>
                </div>
              </div>

              {/* Timer */}
              {!showResult && (
                <div className="mb-4">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="flex items-center gap-2 font-semibold text-gray-700">
                      <Clock className="w-4 h-4" />
                      Time Remaining
                    </span>
                    <span className={"font-bold " + (timeLeft <= 5 ? "text-red-600 animate-pulse" : timeLeft <= 10 ? "text-yellow-600" : "text-green-600")}>
                      {timeLeft}s
                    </span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={timerColor + " h-full transition-all duration-1000 ease-linear"}
                      style={{ width: `${timePercentage}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Progress Bar */}
              <div className="mb-2 flex items-center justify-between text-xs text-gray-600">
                <span>Overall Progress</span>
                <span className="font-semibold text-orange-600">
                  {Math.round(progress)}%
                </span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full mb-4">
                <div
                  className="h-full bg-orange-600 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4">
                  {question.question}
                </h2>

                <div className="space-y-3">
                  {question.options.map((option, index) => {
                    const isSelected = selectedAnswer === option;
                    const isCorrectOption = option === question.correctAnswer;

                    let buttonClass =
                      "w-full text-left p-4 rounded-lg border-2 transition-all ";

                    if (showResult) {
                      if (isCorrectOption) {
                        buttonClass += "bg-green-50 border-green-500 ";
                      } else if (isSelected && !isCorrectOption) {
                        buttonClass += "bg-red-50 border-red-500 ";
                      } else {
                        buttonClass += "bg-gray-50 border-gray-200 opacity-50 ";
                      }
                    } else {
                      if (isSelected) {
                        buttonClass += "bg-orange-50 border-orange-500 ";
                      } else {
                        buttonClass +=
                          "bg-white border-gray-300 hover:border-orange-400 hover:bg-orange-50 ";
                      }
                    }

                    return (
                      <button
                        key={index}
                        onClick={() => handleAnswerSelect(option)}
                        disabled={showResult}
                        className={buttonClass}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-800">
                            {option}
                          </span>
                          {showResult && isCorrectOption && (
                            <Check className="w-5 h-5 text-green-600" />
                          )}
                          {showResult && isSelected && !isCorrectOption && (
                            <X className="w-5 h-5 text-red-600" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {showResult && (
                <div
                  className={
                    "p-4 rounded-lg border-2 mb-6 " +
                    (isCorrect
                      ? "bg-green-50 border-green-500"
                      : "bg-red-50 border-red-500")
                  }
                >
                  <div className="flex items-center gap-2 mb-2">
                    {isCorrect ? (
                      <Check className="w-6 h-6 text-green-600" />
                    ) : (
                      <X className="w-6 h-6 text-red-600" />
                    )}
                    <h3 className="font-bold text-gray-800">
                      {isCorrect ? "Correct!" : timeLeft === 0 && !selectedAnswer ? "Time's Up!" : "Incorrect"}
                    </h3>
                  </div>
                  {isCorrect && (
                    <p className="text-sm text-green-700 mb-2">
                      +{getLevelRewards().coins} coins, +{getLevelRewards().xp} XP
                    </p>
                  )}
                  <p className="text-sm text-gray-700 font-semibold mb-2">
                    Correct Answer: {question.correctAnswer}
                  </p>
                  <p className="text-sm text-gray-700">
                    {question.explanation}
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                {!showResult ? (
                  <button
                    onClick={handleSubmit}
                    disabled={!selectedAnswer}
                    className={
                      "flex-1 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 " +
                      (selectedAnswer
                        ? "bg-orange-600 text-white hover:bg-orange-700"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed")
                    }
                  >
                    Submit Answer
                  </button>
                ) : (
                  <button
                    onClick={handleNext}
                    className="flex-1 bg-orange-600 text-white py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors flex items-center justify-center gap-2"
                  >
                    {currentQuestion + 1 < totalQuestions ? "Next Question" : "View Results"}
                    <ChevronRight className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Round Complete Review Screen
  if (gameMode === "roundComplete") {
    const completedRound = quizRounds[currentRound];
    const isLastRound = currentRound + 1 >= quizRounds.length;
    const roundCorrect = roundAnswers.filter(a => a.isCorrect).length;
    const roundTotal = completedRound.questions.length;

    return (
      <div
        className="min-h-screen"
        style={{
          backgroundImage: "url('/avatar-pubquiz.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="min-h-screen bg-gradient-to-br from-purple-900/80 to-violet-900/80">
          <div className="max-w-4xl mx-auto px-4 py-8">
            <button
              onClick={onBack}
              className="mb-4 text-white hover:text-gray-200 flex items-center gap-2"
            >
              ← Back to Games
            </button>

            {/* Round summary header */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 mb-1">
                    Round {currentRound + 1} Complete!
                  </h1>
                  <p className="text-gray-600">{completedRound.title}</p>
                  {!isLastRound && (
                    <p className="text-sm text-purple-600 mt-1">
                      Next: {quizRounds[currentRound + 1].title}
                    </p>
                  )}
                </div>
                <div className={`bg-gradient-to-br ${completedRound.color} rounded-xl px-6 py-4 text-center border`}>
                  <div className="text-4xl font-bold text-gray-800">{roundCorrect}/{roundTotal}</div>
                  <div className="text-xs text-gray-600 mt-1">correct this round</div>
                </div>
              </div>
            </div>

            {/* Per-question review */}
            <h2 className="text-lg font-bold text-white mb-4">Answer Review</h2>
            <div className="space-y-3 mb-6">
              {roundAnswers.map((answer, index) => (
                <div
                  key={index}
                  className={`bg-white rounded-xl p-4 shadow-sm border-2 ${answer.isCorrect ? "border-green-300" : "border-red-300"}`}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center mt-0.5 ${answer.isCorrect ? "bg-green-100" : "bg-red-100"}`}>
                      {answer.isCorrect ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <X className="w-4 h-4 text-red-600" />
                      )}
                    </div>
                    <p className="font-semibold text-gray-800 text-sm leading-snug flex-1">
                      Q{index + 1}. {answer.question}
                    </p>
                  </div>

                  <div className="ml-10 grid grid-cols-2 gap-1.5 mb-3">
                    {answer.options.map((option, optIdx) => {
                      const isCorrectOption = option === answer.correctAnswer;
                      const isUserWrong = option === answer.selectedAnswer && !isCorrectOption;
                      let optClass = "text-xs px-3 py-1.5 rounded-lg border ";
                      if (isCorrectOption) {
                        optClass += "bg-green-50 border-green-400 text-green-800 font-semibold";
                      } else if (isUserWrong) {
                        optClass += "bg-red-50 border-red-400 text-red-700 line-through";
                      } else {
                        optClass += "bg-gray-50 border-gray-200 text-gray-400";
                      }
                      return (
                        <div key={optIdx} className={optClass}>
                          {option}
                          {isCorrectOption && " ✓"}
                          {isUserWrong && " ✗"}
                        </div>
                      );
                    })}
                  </div>

                  <div className="ml-10 text-xs text-gray-500 italic leading-relaxed">
                    {answer.explanation}
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation buttons */}
            <div className="space-y-3">
              <button
                onClick={handleNextRound}
                className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
              >
                {isLastRound ? "View Final Results" : `Next Round: ${quizRounds[currentRound + 1].title}`}
                <ChevronRight className="w-5 h-5" />
              </button>
              <button
                onClick={onBack}
                className="w-full bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
              >
                Back to Games
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Classic Mode Playing Phase
  const round = quizRounds[currentRound];
  const question = round.questions[currentQuestion];
  const isCorrect = showResult && selectedAnswer === question.correctAnswer;
  const totalQuestions = round.questions.length;
  const totalQuestionsAllRounds = quizRounds.reduce((sum, r) => sum + r.questions.length, 0);
  const questionsCompletedBefore = quizRounds.slice(0, currentRound).reduce((sum, r) => sum + r.questions.length, 0);
  const overallProgress = ((questionsCompletedBefore + currentQuestion + 1) / totalQuestionsAllRounds) * 100;

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundImage: "url('/avatar-pubquiz.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <div className="min-h-screen bg-gradient-to-br from-purple-900/80 to-violet-900/80">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <button
            onClick={handleBackToSelection}
            className="mb-4 text-white hover:text-gray-200 flex items-center gap-2"
          >
            ← Back to Mode Selection
          </button>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              {round.title}
            </h1>
            <p className="text-sm text-gray-600">{round.description}</p>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-600">
              Question {currentQuestion + 1} of {totalQuestions}
            </div>
            <div className="text-lg font-bold text-purple-600">
              Round {currentRound + 1}/4
            </div>
          </div>
        </div>

        <div className="mb-2 flex items-center justify-between text-xs text-gray-600">
          <span>Overall Progress</span>
          <span className="font-semibold text-purple-600">
            {Math.round(overallProgress)}%
          </span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full mb-4">
          <div
            className="h-full bg-purple-600 rounded-full transition-all duration-300"
            style={{ width: `${overallProgress}%` }}
          />
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">
            {question.question}
          </h2>

          <div className="space-y-3">
            {question.options.map((option, index) => {
              const isSelected = selectedAnswer === option;
              const isCorrectOption = option === question.correctAnswer;

              let buttonClass =
                "w-full text-left p-4 rounded-lg border-2 transition-all ";

              if (showResult) {
                if (isCorrectOption) {
                  buttonClass += "bg-green-50 border-green-500 ";
                } else if (isSelected && !isCorrectOption) {
                  buttonClass += "bg-red-50 border-red-500 ";
                } else {
                  buttonClass += "bg-gray-50 border-gray-200 opacity-50 ";
                }
              } else {
                if (isSelected) {
                  buttonClass += "bg-purple-50 border-purple-500 ";
                } else {
                  buttonClass +=
                    "bg-white border-gray-300 hover:border-purple-400 hover:bg-purple-50 ";
                }
              }

              return (
                <button
                  key={index}
                  onClick={() => handleAnswerSelect(option)}
                  disabled={showResult}
                  className={buttonClass}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-800">
                      {option}
                    </span>
                    {showResult && isCorrectOption && (
                      <Check className="w-5 h-5 text-green-600" />
                    )}
                    {showResult && isSelected && !isCorrectOption && (
                      <X className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {showResult && (
          <div
            className={
              "p-4 rounded-lg border-2 mb-6 " +
              (isCorrect
                ? "bg-green-50 border-green-500"
                : "bg-red-50 border-red-500")
            }
          >
            <div className="flex items-center gap-2 mb-2">
              {isCorrect ? (
                <Check className="w-6 h-6 text-green-600" />
              ) : (
                <X className="w-6 h-6 text-red-600" />
              )}
              <h3 className="font-bold text-gray-800">
                {isCorrect ? "Correct!" : "Incorrect"}
              </h3>
            </div>
            {isCorrect && (
              <p className="text-sm text-green-700 mb-2">
                +5 coins, +10 XP
              </p>
            )}
            <p className="text-sm text-gray-700 font-semibold mb-2">
              Correct Answer: {question.correctAnswer}
            </p>
            <p className="text-sm text-gray-700">
              {question.explanation}
            </p>
          </div>
        )}

        <div className="flex gap-3">
          {!showResult ? (
            <button
              onClick={handleSubmit}
              disabled={!selectedAnswer}
              className={
                "flex-1 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 " +
                (selectedAnswer
                  ? "bg-purple-600 text-white hover:bg-purple-700"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed")
              }
            >
              Submit Answer
            </button>
          ) : (
            <button
              onClick={handleNext}
              className="flex-1 bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
            >
              {currentQuestion + 1 < totalQuestions ? "Next Question" : currentRound + 1 < quizRounds.length ? "Next Round" : "View Results"}
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
        </div>
      </div>
    </div>
  );
};

export default PubQuiz;
